import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import JSZip from "jszip";
import { jsPDF } from "jspdf";
import buffer from "@turf/buffer";
import { polygon as turfPolygon } from "@turf/helpers";
import { generateDxf } from "./utils/cad";

import FinancialPage from "./FinancialPage";
import QuotePage from "./QuotePage";
import UserManual from "./UserManual";
import calculateLcoe from "./utils/finance";

import {
  radians,
  enuToLatLon,
  latLonToEnu,
  rotatePoint,
  pointInPoly,
  polygonBounds,
  polygonArea,
  polygonCentroid,
  maxPlacements,
} from "./utils/geo";

// Close a linear ring, avoiding duplicated end points
function closeRing(coords) {
  if (coords.length === 0) return coords;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coords;
  return [...coords, first];
}

// Formatting helpers
const round6 = (n) => Math.round(n * 1e6) / 1e6;
function formatNumber(num, digits = 0) {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
function formatPower(kW) {
  if (kW >= 1000) {
    return { value: formatNumber(kW / 1000, 2), unit: "MW" };
  }
  return { value: formatNumber(kW, 1), unit: "kW" };
}

/* ---------- PV presets (LONGi Hi-MO 7) ---------- */
const MOD_PRESETS = [
  // Typical published sizes; adjust if you have exact datasheets
  { id: "himo7-620", label: "LONGi Hi-MO 7 • 620W (≈2384×1134mm)", L_mm: 2384, W_mm: 1134, P_w: 620 },
  { id: "himo7-720", label: "LONGi Hi-MO 7 • 720W (≈2384×1303mm)", L_mm: 2384, W_mm: 1303, P_w: 720 },
];

const YIELD_FACTORS = { peg: 0.93, fixed: 1.0, tracker: 1.2 };

const DEFAULT_COSTS = [
  {
    id: "main-equipment",
    label: "MAIN EQUIPMENT",
    children: [
      { id: "pv-module", label: "PV module", perKwp: 100 },
      { id: "structure", label: "Structure", perKwp: 87.38 },
      { id: "inverter", label: "Inverter", perKwp: 14.81 },
      { id: "mv-station", label: "MV Station", perKwp: 31.88 },
      { id: "transportation", label: "Transportation", perKwp: 22.16 },
    ],
  },
  {
    id: "civil-works",
    label: "CIVIL WORKS",
    children: [
      { id: "site-conditions", label: "Site Conditions - Clearing & Leveling", perKwp: 8 },
      { id: "foundations", label: "Foundations", perKwp: 4.67 },
      { id: "trenches", label: "Trenches and manholes", perKwp: 3.03 },
      {
        id: "security-control",
        label: "Security and control",
        children: [
          { id: "fencing", label: "Fencing", perKwp: 14.57 },
          { id: "access-gate", label: "Access gate", perKwp: 0 },
          { id: "parameter-lights", label: "Parameter lights", perKwp: 0 },
        ],
      },
    ],
  },
  {
    id: "electrical-system",
    label: "ELECTRICAL SYSTEM",
    children: [
      {
        id: "lv-dc-cabling",
        label: "LV DC Electrical cabling (Strings to Inverter)",
        perKwp: 2.39,
      },
      {
        id: "lv-ac-cabling",
        label: "LV AC Electrical cabling (String Inverters to AC Boxes)",
        perKwp: 10,
      },
      {
        id: "mv-cabling",
        label: "MV Electrical cabling (From Power Station to Substation)",
        perKwp: 22.57,
      },
      { id: "aux-ac", label: "Auxiliary AC Cable for the perimeter", perKwp: 4 },
      { id: "communication", label: "Communication / Monitoring System", perKwp: 4.2 },
      {
        id: "connectors",
        label: "Connectors",
        children: [
          { id: "dc-connectors", label: "DC male and female connectors", perKwp: 0.8 },
          { id: "mv-connectors", label: "MV Connectors", perKwp: 1.3 },
        ],
      },
      { id: "installation", label: "Installation Inverters, Powerstation", perKwp: 60.65 },
    ],
  },
  {
    id: "other-material",
    label: "OTHER MATERIAL",
    children: [
      {
        id: "monitoring-system",
        label: "Monitoring system",
        children: [
          { id: "weather-station", label: "Weather Station", perKwp: 10.42 },
          { id: "ups", label: "UPS", perKwp: 6.54 },
          { id: "scada", label: "SCADA", perKwp: 6.67 },
        ],
      },
      {
        id: "security-system",
        label: "Security and Control System",
        children: [{ id: "lighting", label: "Lighting", perKwp: 2.87 }],
      },
      {
        id: "buildings",
        label: "Buildings",
        children: [{ id: "control-room", label: "Control Room Building", perKwp: 6.8 }],
      },
    ],
  },
  {
    id: "mechanical-works",
    label: "MECHANICAL WORKS",
    children: [
      {
        id: "mechanical-works-sub",
        label: "Mechanical works",
        children: [
          {
            id: "installation-trackers",
            label: "Installation Trackers including claping of Modules",
            perKwp: 75.01,
          },
          {
            id: "commission-service",
            label: "On site comission service for Sungrow Products",
            perKwp: 2.0,
          },
          { id: "ohs", label: "Occupational Health and Safety", perKwp: 2.67 },
          {
            id: "engineering",
            label: "Engineering and Project Management",
            perKwp: 18.33,
          },
          { id: "contingency", label: "Contingency", perKwp: 24.3 },
        ],
      },
    ],
  },
];

const cloneCosts = (items) =>
  items.map((i) => ({
    ...i,
    includeInQuote: false,
    children: i.children ? cloneCosts(i.children) : undefined,
  }));

const createDefaultCosts = () => ({
  peg: cloneCosts(DEFAULT_COSTS),
  fixed: cloneCosts(DEFAULT_COSTS),
  tracker: cloneCosts(DEFAULT_COSTS),
});

const sumPerKwp = (items) =>
  items.reduce(
    (sum, i) => sum + (i.children ? sumPerKwp(i.children) : i.perKwp),
    0
  );

const DEFAULT_SYSTEMS = {
  peg: {
    presetId: MOD_PRESETS[0].id,
    landscape: true,
    modulesW: 25,
    modulesH: 15,
    clampGapMm: 20,
    gapNS: 2,
    gapEW: 2,
    bufferM: 0,
    orientationDeg: 90,
    alignGrid: false,
    specificYield: 1862,
    yieldMode: "auto",
    fillBlocks: 0,
    fillOrigin: "SW",
    fillAll: false,
  },
  fixed: {
    presetId: MOD_PRESETS[0].id,
    landscape: false,
    modulesW: 24,
    modulesH: 2,
    clampGapMm: 20,
    gapNS: 5,
    gapEW: 2,
    bufferM: 0,
    orientationDeg: 0,
    alignGrid: false,
    specificYield: 1600,
    yieldMode: "auto",
    fillBlocks: 0,
    fillOrigin: "SW",
    fillAll: false,
  },
  tracker: {
    presetId: MOD_PRESETS[0].id,
    landscape: true,
    modulesW: 60,
    modulesH: 1,
    clampGapMm: 20,
    gapNS: 7,
    gapEW: 1,
    bufferM: 0,
    orientationDeg: 90,
    alignGrid: false,
    specificYield: 2456,
    yieldMode: "auto",
    fillBlocks: 0,
    fillOrigin: "SW",
    fillAll: false,
  },
};

const DEFAULT_FINANCIALS = {
  peg: { landCostPerHa: 0, oAndMPerKwp: 0, financingRate: 0.08, annualDegradation: 0.5, lifetimeYears: 25 },
  fixed: { landCostPerHa: 0, oAndMPerKwp: 0, financingRate: 0.08, annualDegradation: 0.5, lifetimeYears: 25 },
  tracker: { landCostPerHa: 0, oAndMPerKwp: 0, financingRate: 0.08, annualDegradation: 0.5, lifetimeYears: 25 },
};

export default function App() {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const polygonLayerRef = useRef(null);
  const tablesLayerRef = useRef(null);
  const labelLayerRef = useRef(null);
  const moduleLayerRef = useRef(null);
  const blockLabelLayerRef = useRef(null);
  const bufferLayerRef = useRef(null);
  const outlineDimLayerRef = useRef(null);
  const handleMarkersRef = useRef([]);
  const placementsRef = useRef([]); // store latest placements for metrics
  const redrawTablesRef = useRef(() => {}); // always call the latest redraw routine

  // --- Defaults ---
  // LSMFEZ approx center
  const DEFAULT_CENTER = [-15.501331, 28.397618];

  // Polygon [lat, lng] — start with a single point so you can move it anywhere
  const [polygon, setPolygon] = useState([
    [-15.499085, 28.395754], // NW
    [-15.499085, 28.399482], // NE
    [-15.503577, 28.399482], // SE
    [-15.503577, 28.395754], // SW
  ]);
  const [autoFit, setAutoFit] = useState(true);

  // Project name
  const [projectName, setProjectName] = useState("");

  // Last time the project was exported
  const [savedAt, setSavedAt] = useState(null);

  // Add-point mode
  const [addMode, setAddMode] = useState(false);
  const addModeRef = useRef(addMode);
  useEffect(() => {
    addModeRef.current = addMode;
  }, [addMode]);

  // System type + per-system settings
  const SYSTEM_LABELS = {
    peg: "PEG blocks",
    fixed: "Fixed tilt N/S",
    tracker: "Tracker",
  };
  const [systemType, setSystemType] = useState("peg");
  const [systems, setSystems] = useState(DEFAULT_SYSTEMS);
  const [costs, setCosts] = useState(createDefaultCosts());
  const [financials, setFinancials] = useState(DEFAULT_FINANCIALS);
  const [currency, setCurrency] = useState("ZAR");
  const [page, setPage] = useState("design");

  // PV settings (defaults updated)
  const [presetId, setPresetId] = useState(MOD_PRESETS[0].id);
  const preset = useMemo(
    () => MOD_PRESETS.find((p) => p.id === presetId) || MOD_PRESETS[0],
    [presetId]
  );
  const [landscape, setLandscape] = useState(true);
  const [modulesW, setModulesW] = useState(25); // default 25 x 15
  const [modulesH, setModulesH] = useState(15);
  const [pegBlockPreset, setPegBlockPreset] = useState("custom");
  const [clampGapMm, setClampGapMm] = useState(20);
  const [orientationDeg, setOrientationDeg] = useState(90); // E–W packing axis
  const [alignGrid, setAlignGrid] = useState(false); // keep rows/cols aligned
  const [optMetric, setOptMetric] = useState("gcr");
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [fillBlocks, setFillBlocks] = useState(0); // number of blocks to place
  const [maxFillBlocks, setMaxFillBlocks] = useState(0); // maximum available blocks
  const [fillOrigin, setFillOrigin] = useState("SW"); // starting corner for fill
  const [fillAll, setFillAll] = useState(false); // fill all available blocks

  // Zoom thresholds
  const PANEL_ZOOM = 21; // show individual modules at this zoom level
  const LABEL_ZOOM = 20; // show block labels at this zoom level

  // Derived table size (meters)
  const modL_m = (landscape ? preset.W_mm : preset.L_mm) / 1000; // along row (E–W)
  const modW_m = (landscape ? preset.L_mm : preset.W_mm) / 1000; // N–S depth
  const clamp_m = clampGapMm / 1000;
  const tableW_m = modulesW * modL_m + (modulesW - 1) * clamp_m; // E–W width
  const tableH_m = modulesH * modW_m + (modulesH - 1) * clamp_m; // N–S depth

  // Pitch and spacing between table edges (defaults 2m each)
  const [gapNS, setGapNS] = useState(2); // row pitch (m)
  const [gapEW, setGapEW] = useState(2); // east–west gap (m)
  const [bufferM, setBufferM] = useState(0); // outline buffer (m)

  // Keep fill count in sync when fillAll is enabled
  useEffect(() => {
    if (fillAll && fillBlocks !== maxFillBlocks) {
      setFillBlocks(maxFillBlocks);
    }
  }, [fillAll, maxFillBlocks, fillBlocks]);

  // Auto-select PEG block preset based on current module dimensions
  useEffect(() => {
    if (systemType !== "peg") return;
    if (modulesW === 8 && modulesH === 12) setPegBlockPreset("8x12");
    else if (modulesW === 14 && modulesH === 24) setPegBlockPreset("14x24");
    else setPegBlockPreset("custom");
  }, [systemType, modulesW, modulesH]);

  // Optimization constraints
  const [optLocks, setOptLocks] = useState({
    presetId: true,
    landscape: true,
    modulesW: true,
    modulesH: true,
    gapNS: true,
    gapEW: true,
    orientationDeg: true,
  });
  const [optRanges, setOptRanges] = useState({
    modulesW: { min: modulesW, max: modulesW },
    modulesH: { min: modulesH, max: modulesH },
    gapNS: { min: gapNS, max: gapNS },
    gapEW: { min: gapEW, max: gapEW },
    orientationDeg: { min: orientationDeg, max: orientationDeg },
  });

  useEffect(() => {
    if (optLocks.modulesW)
      setOptRanges((r) => ({ ...r, modulesW: { min: modulesW, max: modulesW } }));
  }, [modulesW, optLocks.modulesW]);
  useEffect(() => {
    if (optLocks.modulesH)
      setOptRanges((r) => ({ ...r, modulesH: { min: modulesH, max: modulesH } }));
  }, [modulesH, optLocks.modulesH]);
  useEffect(() => {
    if (optLocks.gapNS)
      setOptRanges((r) => ({ ...r, gapNS: { min: gapNS, max: gapNS } }));
  }, [gapNS, optLocks.gapNS]);
  useEffect(() => {
    if (optLocks.gapEW)
      setOptRanges((r) => ({ ...r, gapEW: { min: gapEW, max: gapEW } }));
  }, [gapEW, optLocks.gapEW]);
  useEffect(() => {
    if (optLocks.orientationDeg)
      setOptRanges((r) => ({
        ...r,
        orientationDeg: { min: orientationDeg, max: orientationDeg },
      }));
  }, [orientationDeg, optLocks.orientationDeg]);

  const boundRow = (field, label, step = 1) => (
    <div
      className="bound-row"
      style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}
    >
      <label style={{ flex: 1 }}>
        <input
          type="checkbox"
          checked={optLocks[field]}
          onChange={(e) =>
            setOptLocks((prev) => ({ ...prev, [field]: e.target.checked }))
          }
        />
        {label}
      </label>
      <input
        type="number"
        step={step}
        value={optRanges[field].min}
        onChange={(e) =>
          setOptRanges((prev) => ({
            ...prev,
            [field]: { ...prev[field], min: +e.target.value },
          }))
        }
        disabled={optLocks[field]}
        style={{ width: 50 }}
      />
      <input
        type="number"
        step={step}
        value={optRanges[field].max}
        onChange={(e) =>
          setOptRanges((prev) => ({
            ...prev,
            [field]: { ...prev[field], max: +e.target.value },
          }))
        }
        disabled={optLocks[field]}
        style={{ width: 50 }}
      />
    </div>
  );

  // Derived center-to-center pitches
  const rowPitchM = useMemo(
    () =>
      systemType === "peg"
        ? tableH_m + gapNS
        : Math.max(tableH_m, gapNS, 0.1),
    [systemType, tableH_m, gapNS]
  );
  const columnPitchM = useMemo(
    () => tableW_m + gapEW,
    [tableW_m, gapEW]
  );

  function calcMetricsFor(params) {
    const cfg = {
      presetId,
      landscape,
      modulesW,
      modulesH,
      gapNS,
      gapEW,
      bufferM,
      orientationDeg,
      alignGrid,
      specificYield,
      ...params,
    };
    const presetEval =
      MOD_PRESETS.find((p) => p.id === cfg.presetId) || MOD_PRESETS[0];
    const modL = (cfg.landscape ? presetEval.W_mm : presetEval.L_mm) / 1000;
    const modW = (cfg.landscape ? presetEval.L_mm : presetEval.W_mm) / 1000;
    const clamp = clampGapMm / 1000;
    const tableW = cfg.modulesW * modL + (cfg.modulesW - 1) * clamp;
    const tableH = cfg.modulesH * modW + (cfg.modulesH - 1) * clamp;
    const moduleWidth = Math.min(modL, modW);
    const rowStep =
      systemType === "peg"
        ? Math.max(tableH + cfg.gapNS, 0.1)
        : Math.max(cfg.gapNS, tableH, 0.1);
    const colStep = Math.max(tableW + cfg.gapEW, 0.1);
    const halfW = tableW / 2;
    const halfH = tableH / 2;
    const polyBase = polygon;
    let poly = polyBase;
    if (cfg.bufferM > 0 && polyBase.length >= 3) {
      try {
        const ringBase = closeRing(polyBase.map(([lat, lng]) => [lng, lat]));
        const tPoly = turfPolygon([ringBase]);
        const buff = buffer(tPoly, -cfg.bufferM, { units: "meters" });
        let coords;
        if (buff && buff.geometry) {
          if (buff.geometry.type === "Polygon")
            coords = buff.geometry.coordinates[0];
          else if (buff.geometry.type === "MultiPolygon")
            coords = buff.geometry.coordinates[0][0];
        }
        if (coords && coords.length >= 4) {
          const inner = coords.slice(0, -1);
          poly = inner.map(([lng, lat]) => [lat, lng]);
        }
      } catch (err) {
        console.error("buffer failed", err);
      }
    }

    const [lon0, lat0] = polygonCentroid(poly.map(([lat, lon]) => [lon, lat]));
    const polyENU = poly.map(([lat, lng]) => latLonToEnu(lat, lng, lat0, lon0));
    const ang = radians(cfg.orientationDeg);
    const polyLocal = polyENU.map((p) => rotatePoint(p, -ang));
    const placements = maxPlacements(
      polyLocal,
      rowStep,
      colStep,
      halfW,
      halfH,
      cfg.alignGrid
    );
    const modulesPerTable = cfg.modulesW * cfg.modulesH;
    const tables = placements;
    const modules = tables * modulesPerTable;
    const kWp = (modules * presetEval.P_w) / 1000;
    const siteArea_m2 = polygonArea(polyENU);
    const moduleArea_m2 = modules * (modL * modW);
    let gcr;
    if (systemType === "peg") {
      gcr = siteArea_m2 > 0 ? moduleArea_m2 / siteArea_m2 : 0;
    } else {
      const baseWidth = systemType === "tracker" ? moduleWidth : tableH;
      gcr = rowStep > 0 ? baseWidth / rowStep : 0;
    }
    const kWpPerHa = siteArea_m2 > 0 ? kWp / (siteArea_m2 / 10000) : 0;
    const annualMWh = (kWp * cfg.specificYield) / 1000;
    return { tables, modules, kWp, siteArea_m2, gcr, kWpPerHa, annualMWh };
  }

  function optimizeLayout(metric) {
    const base = {
      presetId,
      landscape,
      modulesW,
      modulesH,
      gapNS,
      gapEW,
      orientationDeg,
      alignGrid,
      specificYield,
    };
    const step = { orientationDeg: 5, modulesW: 1, modulesH: 1, gapNS: 0.5, gapEW: 0.5 };
    const iterations = 20;
    const attempts = 5;
    const presetChoices = optLocks.presetId
      ? [presetId]
      : MOD_PRESETS.map((p) => p.id);
    const landChoices = optLocks.landscape ? [landscape] : [true, false];

    function run(start) {
      let params = { ...start };
      for (let i = 0; i < iterations; i++) {
        let improved = false;
        let best = params;
        let bestVal = calcMetricsFor(params)[metric];
        for (const land of landChoices) {
          for (const p of presetChoices) {
            const trial = { ...params, landscape: land, presetId: p };
            const val = calcMetricsFor(trial)[metric];
            if (val > bestVal) {
              bestVal = val;
              best = trial;
              improved = true;
            }
          }
        }
        for (const key of Object.keys(step)) {
          if (optLocks[key]) continue;
          for (const dir of [-1, 1]) {
            const trial = { ...best, [key]: best[key] + dir * step[key] };
            const range = optRanges[key];
            if (range) {
              trial[key] = Math.min(range.max, Math.max(range.min, trial[key]));
            }
            const val = calcMetricsFor(trial)[metric];
            if (val > bestVal) {
              bestVal = val;
              best = trial;
              improved = true;
            }
          }
        }
        params = best;
        params.orientationDeg = ((params.orientationDeg % 360) + 360) % 360;
        params.modulesW = Math.max(1, Math.round(params.modulesW));
        params.modulesH = Math.max(1, Math.round(params.modulesH));
        params.gapNS = Math.max(0, params.gapNS);
        params.gapEW = Math.max(0, params.gapEW);
        if (!improved) break;
      }
      return { params, metric: calcMetricsFor(params)[metric] };
    }

    let bestRun = null;
    let bestVal = -Infinity;
    for (let a = 0; a < attempts; a++) {
      const start = { ...base };
      if (a > 0) {
        for (const key of Object.keys(step)) {
          if (optLocks[key]) continue;
          const range = optRanges[key];
          if (range) {
            start[key] = range.min + Math.random() * (range.max - range.min);
          } else {
            start[key] += (Math.random() * 2 - 1) * step[key] * 5;
          }
        }
        start.orientationDeg = Math.random() * 360;
      }
      const res = run(start);
      const metrics = calcMetricsFor(res.params);
      if (metrics.modules > 0 && res.metric > bestVal) {
        bestVal = res.metric;
        bestRun = res.params;
      }
    }

    if (bestRun) {
      setPresetId(bestRun.presetId);
      setLandscape(bestRun.landscape);
      setModulesW(bestRun.modulesW);
      setModulesH(bestRun.modulesH);
      setGapNS(bestRun.gapNS);
      setGapEW(bestRun.gapEW);
      setOrientationDeg(bestRun.orientationDeg);
      setSpecificYield(bestRun.specificYield);
    }
  }

  // Specific yield (kWh/kWp·yr)
  const [specificYield, setSpecificYield] = useState(2000);
  const [yieldMode, setYieldMode] = useState("auto");
  const [baseYield, setBaseYield] = useState(2000);
  const polyCenter = useMemo(() => {
    const [lon, lat] = polygonCentroid(
      polygon.map(([lat, lon]) => [lon, lat])
    );
    return [lat, lon];
  }, [polygon]);

  useEffect(() => {
    if (!polyCenter) return;
    (async () => {
      try {
        const resp = await fetch(
          `https://developer.nrel.gov/api/pvwatts/v6.json?api_key=DEMO_KEY&lat=${polyCenter[0]}&lon=${polyCenter[1]}&system_capacity=1&module_type=1&array_type=1&tilt=${Math.abs(
            polyCenter[0]
          )}&azimuth=180&losses=14`
        );
        const data = await resp.json();
        const val = data?.outputs?.ac_annual;
        if (val) setBaseYield(val);
      } catch (err) {
        console.error("Failed to fetch specific yield", err);
      }
    })();
  }, [polyCenter]);

  useEffect(() => {
    setSystems((prev) => ({
      ...prev,
      peg: {
        ...prev.peg,
        ...(prev.peg.yieldMode === "auto" && {
          specificYield: baseYield * YIELD_FACTORS.peg,
        }),
      },
      fixed: {
        ...prev.fixed,
        ...(prev.fixed.yieldMode === "auto" && {
          specificYield: baseYield * YIELD_FACTORS.fixed,
        }),
      },
      tracker: {
        ...prev.tracker,
        ...(prev.tracker.yieldMode === "auto" && {
          specificYield: baseYield * YIELD_FACTORS.tracker,
        }),
      },
    }));
    if (yieldMode === "auto") {
      setSpecificYield(baseYield * YIELD_FACTORS[systemType]);
    }
  }, [baseYield, systemType, yieldMode]);

  // Metrics
  const [metrics, setMetrics] = useState({
    tables: 0,
    modules: 0,
    kWp: 0,
    siteArea_m2: 0,
    gcr: 0,
    kWpPerHa: 0,
    annualMWh: 0
  });
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const costPerKwp = useMemo(
    () => sumPerKwp(costs[systemType]),
    [costs, systemType]
  );
  const totalCapex = costPerKwp * metrics.kWp;
  const costPerKwhYear = metrics.annualMWh > 0 ? totalCapex / (metrics.annualMWh * 1000) : 0;
  const siteAreaHa = metrics.siteArea_m2 / 10000;
  const { lcoe } = calculateLcoe(
    { capex: totalCapex, annualMWh: metrics.annualMWh, siteAreaHa, kWp: metrics.kWp },
    financials[systemType]
  );

  // Map zoom (for debugging display)
  const [zoom, setZoom] = useState(18);

  function applyPVSettings(s) {
    if (!s) return;
    if (s.presetId) setPresetId(s.presetId);
    if (typeof s.landscape === "boolean") setLandscape(s.landscape);
    if (s.modulesW !== undefined) setModulesW(s.modulesW);
    if (s.modulesH !== undefined) setModulesH(s.modulesH);
    if (s.clampGapMm !== undefined) setClampGapMm(s.clampGapMm);
    if (s.gapNS !== undefined) setGapNS(s.gapNS);
    if (s.gapEW !== undefined) setGapEW(s.gapEW);
    if (s.bufferM !== undefined) setBufferM(s.bufferM);
    if (s.orientationDeg !== undefined) setOrientationDeg(s.orientationDeg);
    if (typeof s.alignGrid === "boolean") setAlignGrid(s.alignGrid);
    if (s.specificYield !== undefined) setSpecificYield(s.specificYield);
    if (s.fillBlocks !== undefined) setFillBlocks(s.fillBlocks);
    if (s.fillOrigin) setFillOrigin(s.fillOrigin);
    if (typeof s.fillAll === "boolean") setFillAll(s.fillAll);
    setYieldMode(s.yieldMode || "auto");
  }

  function handleSystemChange(t) {
    const cfg = systems[t] || DEFAULT_SYSTEMS[t];
    setSystemType(t);
    applyPVSettings(cfg);
  }

  // Persist per-system settings when they change
  useEffect(() => {
    setSystems((prev) => ({
      ...prev,
      [systemType]: {
        ...prev[systemType],
        presetId,
        landscape,
        modulesW,
        modulesH,
        clampGapMm,
        gapNS,
        gapEW,
        bufferM,
        orientationDeg,
        alignGrid,
        specificYield,
        yieldMode,
        fillBlocks,
        fillOrigin,
        fillAll,
      },
    }));
  }, [
    systemType,
    presetId,
    landscape,
    modulesW,
    modulesH,
    clampGapMm,
    gapNS,
    gapEW,
    bufferM,
    orientationDeg,
    alignGrid,
    specificYield,
    yieldMode,
    fillBlocks,
    fillOrigin,
    fillAll,
  ]);

  const buildProject = useCallback(
    (extra = {}) => ({
      name: projectName,
      systemType,
      systems,
      costs,
      currency,
      polygon,
      autoFit,
      presetId,
      landscape,
      modulesW,
      modulesH,
      clampGapMm,
      gapNS,
      gapEW,
      bufferM,
      orientationDeg,
      alignGrid,
      specificYield,
      yieldMode,
      fillBlocks,
      fillOrigin,
      fillAll,
      savedAt,
      ...extra,
    }),
    [
      projectName,
      systemType,
      systems,
      costs,
      currency,
      polygon,
      autoFit,
      presetId,
      landscape,
      modulesW,
      modulesH,
      clampGapMm,
      gapNS,
      gapEW,
      bufferM,
      orientationDeg,
      alignGrid,
      specificYield,
      yieldMode,
      fillBlocks,
      fillOrigin,
      fillAll,
      savedAt,
    ]
  );

  function applyProject(p) {
    if (!p) return;
    if (p.name !== undefined) setProjectName(p.name);
    if (p.systems)
      setSystems({
        peg: { ...DEFAULT_SYSTEMS.peg, ...(p.systems.peg || {}) },
        fixed: { ...DEFAULT_SYSTEMS.fixed, ...(p.systems.fixed || {}) },
        tracker: { ...DEFAULT_SYSTEMS.tracker, ...(p.systems.tracker || {}) },
      });
    if (p.costs) {
      const mergeCosts = (saved = [], defaults = DEFAULT_COSTS) =>
        defaults.map((d) => {
          const found = saved.find((i) => i.id === d.id) || {};
          return {
            ...d,
            perKwp: d.children
              ? undefined
              : found.perKwp !== undefined
              ? found.perKwp
              : d.perKwp,
            children: d.children
              ? mergeCosts(found.children || [], d.children)
              : undefined,
          };
        });
      setCosts({
        peg: mergeCosts(p.costs.peg),
        fixed: mergeCosts(p.costs.fixed),
        tracker: mergeCosts(p.costs.tracker),
      });
    }
    if (p.currency) setCurrency(p.currency);
    if (p.systemType) setSystemType(p.systemType);
    if (p.polygon)
      setPolygon(p.polygon.map(([a, b]) => [round6(a), round6(b)]));
    if (p.autoFit !== undefined) setAutoFit(p.autoFit);
    if (p.presetId) setPresetId(p.presetId);
    if (typeof p.landscape === "boolean") setLandscape(p.landscape);
    if (p.modulesW !== undefined) setModulesW(p.modulesW);
    if (p.modulesH !== undefined) setModulesH(p.modulesH);
    if (p.clampGapMm !== undefined) setClampGapMm(p.clampGapMm);
    if (p.gapNS !== undefined) setGapNS(p.gapNS);
    if (p.gapEW !== undefined) setGapEW(p.gapEW);
    if (p.bufferM !== undefined) setBufferM(p.bufferM);
    if (p.orientationDeg !== undefined) setOrientationDeg(p.orientationDeg);
    if (typeof p.alignGrid === "boolean") setAlignGrid(p.alignGrid);
    if (p.specificYield !== undefined) setSpecificYield(p.specificYield);
    if (p.yieldMode) setYieldMode(p.yieldMode);
    if (p.fillBlocks !== undefined) setFillBlocks(p.fillBlocks);
    if (p.fillOrigin) setFillOrigin(p.fillOrigin);
    if (typeof p.fillAll === "boolean") setFillAll(p.fillAll);
    if (p.savedAt) setSavedAt(p.savedAt);
  }

  useEffect(() => {
    const saved = localStorage.getItem("pegProject");
    if (saved) {
      try {
        applyProject(JSON.parse(saved));
      } catch (err) {
        console.error("Failed to parse project", err);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("pegProject", JSON.stringify(buildProject()));
  }, [buildProject]);

  useEffect(() => {
    document.title = projectName ? `${projectName} - PEG Layouts` : "PEG Layouts";
  }, [projectName]);

  function downloadProject() {
    const now = new Date();
    const iso = now.toISOString();
    const datePart = now
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "");
    const timePart = now
      .toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(":", "");
    const project = buildProject({ savedAt: iso });
    const blob = new Blob([JSON.stringify(project, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${projectName || "project"}_${datePart}_${timePart}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSavedAt(iso);
  }

  function downloadKmz() {
    if (polygon.length < 3) return;
    const [lon0, lat0] = polygonCentroid(
      polygon.map(([lat, lon]) => [lon, lat])
    );
    const ang = radians(orientationDeg);

    const outlineCoords = [...polygon, polygon[0]]
      .map(([lat, lng]) => `${lng},${lat},0`)
      .join(" ");

    const blockPlacemarks = placementsRef.current
      .map((rectLocal, i) => {
        const ring = rectLocal.map((pt) => {
          const ptENU = rotatePoint(pt, ang);
          const [lat, lon] = enuToLatLon(ptENU[0], ptENU[1], lat0, lon0);
          return `${lon},${lat},0`;
        });
        ring.push(ring[0]);
        const coords = ring.join(" ");
        return `<Placemark><name>Block ${i + 1}</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>`;
      })
      .join("\n");

    const kml = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n<Placemark><name>Outline</name><Polygon><outerBoundaryIs><LinearRing><coordinates>${outlineCoords}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>\n${blockPlacemarks}\n</Document>\n</kml>`;

    const zip = new JSZip();
    zip.file("doc.kml", kml);
    zip.generateAsync({ type: "blob" }).then((content) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${projectName || "project"}.kmz`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  function buildCadGeometry() {
    if (polygon.length < 3) return null;
    const [lon0, lat0] = polygonCentroid(
      polygon.map(([lat, lon]) => [lon, lat])
    );
    if (!isFinite(lat0) || !isFinite(lon0)) return null;

    const outlineEnu = polygon.map(([lat, lng]) =>
      latLonToEnu(lat, lng, lat0, lon0)
    );

    let bufferEnu = null;
    if (bufferM > 0 && polygon.length >= 3) {
      try {
        const ringBase = closeRing(polygon.map(([lat, lng]) => [lng, lat]));
        const tPoly = turfPolygon([ringBase]);
        const buff = buffer(tPoly, -bufferM, { units: "meters" });
        let coords;
        if (buff && buff.geometry) {
          if (buff.geometry.type === "Polygon") coords = buff.geometry.coordinates[0];
          else if (buff.geometry.type === "MultiPolygon")
            coords = buff.geometry.coordinates[0][0];
        }
        if (coords && coords.length >= 4) {
          const inner = coords.slice(0, -1);
          bufferEnu = inner.map(([lng, lat]) => latLonToEnu(lat, lng, lat0, lon0));
        }
      } catch (err) {
        console.error("buffer failed", err);
      }
    }

    const ang = radians(orientationDeg);
    const placements = placementsRef.current || [];
    const placementsEnu = placements.map((rectLocal) =>
      rectLocal.map((pt) => rotatePoint(pt, ang))
    );
    return { outlineEnu, bufferEnu, placementsEnu };
  }

  function downloadCad(format) {
    const cad = buildCadGeometry();
    if (!cad) return;
    const dxf = generateDxf(cad);
    if (!dxf) return;
    const baseName = projectName || "project";
    if (format === "dxf") {
      const blob = new Blob([dxf], { type: "application/dxf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.dxf`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    const zip = new JSZip();
    zip.file(`${baseName}.dxf`, dxf);
    zip.file(
      "README.txt",
      "This bundle contains an AutoCAD-compatible DXF export of the layout. " +
        "Use the DXF file inside this archive when importing into CAD tools."
    );
    zip.generateAsync({ type: "blob" }).then((content) => {
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.dwg.zip`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  function buildLayoutSketch() {
    if (polygon.length === 0) return null;

    const [lon0, lat0] = polygonCentroid(
      polygon.map(([lat, lon]) => [lon, lat])
    );
    if (!isFinite(lat0) || !isFinite(lon0)) return null;

    const polyEnu = polygon.map(([lat, lng]) =>
      latLonToEnu(lat, lng, lat0, lon0)
    );
    const ang = radians(orientationDeg);
    const placements = placementsRef.current || [];
    const placementsEnu = placements.map((rect) =>
      rect.map((pt) => rotatePoint(pt, ang))
    );
    const blockKwp = (modulesW * modulesH * preset.P_w) / 1000;

    const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    const expandBounds = ([x, y]) => {
      if (x < bounds.minX) bounds.minX = x;
      if (x > bounds.maxX) bounds.maxX = x;
      if (y < bounds.minY) bounds.minY = y;
      if (y > bounds.maxY) bounds.maxY = y;
    };
    polyEnu.forEach(expandBounds);
    placementsEnu.forEach((rect) => rect.forEach(expandBounds));

    if (!isFinite(bounds.minX) || !isFinite(bounds.maxX) || bounds.minX === bounds.maxX) {
      const fallback = polyEnu[0];
      if (!fallback) return null;
      bounds.minX = fallback[0] - 5;
      bounds.maxX = fallback[0] + 5;
    }
    if (!isFinite(bounds.minY) || !isFinite(bounds.maxY) || bounds.minY === bounds.maxY) {
      const fallback = polyEnu[0];
      if (!fallback) return null;
      bounds.minY = fallback[1] - 5;
      bounds.maxY = fallback[1] + 5;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1400;
    canvas.height = 900;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const width = bounds.maxX - bounds.minX;
    const height = bounds.maxY - bounds.minY;
    const scale = 0.82 * Math.min(canvas.width / width, canvas.height / height);
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    const projectPoint = ([x, y]) => ({
      x: canvas.width / 2 + (x - centerX) * scale,
      y: canvas.height / 2 - (y - centerY) * scale,
    });

    const outlineCanvas = polyEnu.map(projectPoint);

    // Draw construction grid
    const approxSpacing = Math.max(width, height) / 8;
    const base = 10 ** Math.floor(Math.log10(Math.max(approxSpacing, 1)));
    const candidates = [1, 2, 5, 10];
    const candidate = candidates.find((c) => c * base >= approxSpacing) || 10;
    const spacing = candidate * base;

    ctx.lineWidth = 1;
    ctx.strokeStyle = "#e5e7eb";
    ctx.setLineDash([6, 6]);
    for (let x = Math.ceil(bounds.minX / spacing) * spacing; x <= bounds.maxX; x += spacing) {
      const p1 = projectPoint([x, bounds.minY]);
      const p2 = projectPoint([x, bounds.maxY]);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    for (let y = Math.ceil(bounds.minY / spacing) * spacing; y <= bounds.maxY; y += spacing) {
      const p1 = projectPoint([bounds.minX, y]);
      const p2 = projectPoint([bounds.maxX, y]);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Draw polygon footprint
    if (polyEnu.length > 0) {
      ctx.beginPath();
      const start = projectPoint(polyEnu[0]);
      ctx.moveTo(start.x, start.y);
      for (let i = 1; i < polyEnu.length; i++) {
        const p = projectPoint(polyEnu[i]);
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fillStyle = "rgba(59,130,246,0.08)";
      ctx.strokeStyle = "#1d4ed8";
      ctx.lineWidth = 3.5;
      ctx.fill();
      ctx.stroke();
    }

    // Draw placements
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = "#dc2626";
    ctx.fillStyle = "rgba(220,38,38,0.18)";
    placementsEnu.forEach((rect) => {
      if (!rect.length) return;
      ctx.beginPath();
      const canvasRect = rect.map(projectPoint);
      const start = canvasRect[0];
      ctx.moveTo(start.x, start.y);
      for (let i = 1; i < canvasRect.length; i++) {
        const p = canvasRect[i];
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      const center = canvasRect.reduce(
        (acc, pt) => ({ x: acc.x + pt.x, y: acc.y + pt.y }),
        { x: 0, y: 0 }
      );
      center.x /= canvasRect.length;
      center.y /= canvasRect.length;
      const edgeA = Math.hypot(
        canvasRect[1].x - canvasRect[0].x,
        canvasRect[1].y - canvasRect[0].y
      );
      const edgeB = Math.hypot(
        canvasRect[3].x - canvasRect[0].x,
        canvasRect[3].y - canvasRect[0].y
      );
      const shortest = Math.max(32, Math.min(edgeA, edgeB));
      const fontSize = Math.max(18, Math.min(36, shortest * 0.32));
      ctx.save();
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const kWpDecimals = blockKwp >= 100 ? 0 : blockKwp >= 10 ? 1 : 2;
      const label = `${formatNumber(blockKwp, kWpDecimals)} kWp`;
      const metrics = ctx.measureText(label);
      const paddingX = fontSize * 0.6;
      const paddingY = fontSize * 0.45;
      const boxWidth = metrics.width + paddingX * 2;
      const boxHeight = fontSize + paddingY * 2;
      const boxX = center.x - boxWidth / 2;
      const boxY = center.y - boxHeight / 2;
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.strokeStyle = "rgba(15,23,42,0.55)";
      ctx.lineWidth = 1.4;
      const cornerRadius = Math.min(12, fontSize * 0.6);
      ctx.beginPath();
      if (typeof ctx.roundRect === "function") {
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, cornerRadius);
      } else {
        const r = cornerRadius;
        ctx.moveTo(boxX + r, boxY);
        ctx.lineTo(boxX + boxWidth - r, boxY);
        ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + r);
        ctx.lineTo(boxX + boxWidth, boxY + boxHeight - r);
        ctx.quadraticCurveTo(
          boxX + boxWidth,
          boxY + boxHeight,
          boxX + boxWidth - r,
          boxY + boxHeight
        );
        ctx.lineTo(boxX + r, boxY + boxHeight);
        ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - r);
        ctx.lineTo(boxX, boxY + r);
        ctx.quadraticCurveTo(boxX, boxY, boxX + r, boxY);
        ctx.closePath();
      }
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#0f172a";
      ctx.fillText(label, center.x, center.y);
      ctx.restore();
    });

    // North arrow
    ctx.save();
    ctx.translate(canvas.width - 120, 160);
    ctx.strokeStyle = "#111827";
    ctx.fillStyle = "#111827";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(0, 60);
    ctx.lineTo(0, -60);
    ctx.moveTo(-18, -35);
    ctx.lineTo(0, -60);
    ctx.lineTo(18, -35);
    ctx.stroke();
    ctx.font = "bold 42px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("N", 0, -80);
    ctx.beginPath();
    ctx.moveTo(0, 60);
    ctx.lineTo(0, 110);
    ctx.stroke();
    ctx.font = "16px sans-serif";
    ctx.fillText("0", -20, 128);
    ctx.fillText(`${formatNumber(spacing, spacing >= 10 ? 0 : 1)} m`, 52, 128);
    ctx.restore();

    return {
      dataUrl: canvas.toDataURL("image/png"),
      bounds: { ...bounds, width, height },
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      outlineCanvas,
      outlineEnu: polyEnu,
    };
  }

  async function fetchSatelliteSnapshot() {
    if (polygon.length === 0) return null;
    const placements = placementsRef.current || [];
    const [lon0, lat0] = polygonCentroid(
      polygon.map(([lat, lon]) => [lon, lat])
    );
    if (!isFinite(lat0) || !isFinite(lon0)) return null;
    const ang = radians(orientationDeg);

    const placementsEnu = placements.map((rectLocal) =>
      rectLocal.map((pt) => rotatePoint(pt, ang))
    );
    const placementsLatLng = placementsEnu.map((rectEnu) =>
      rectEnu.map(([east, north]) => {
        const [lat, lon] = enuToLatLon(east, north, lat0, lon0);
        return [lat, lon];
      })
    );
    const halfW = tableW_m / 2;
    const halfH = tableH_m / 2;
    const panelFootprintsLatLng =
      modulesW > 0 && modulesH > 0
        ? placements.map((rectLocal) => {
            if (!Array.isArray(rectLocal) || rectLocal.length < 4) return [];
            const ctrLocal = [
              (rectLocal[0][0] + rectLocal[2][0]) / 2,
              (rectLocal[0][1] + rectLocal[2][1]) / 2,
            ];
            const panels = [];
            for (let r = 0; r < modulesH; r += 1) {
              const yCtr = ctrLocal[1] - halfH + modW_m / 2 + r * (modW_m + clamp_m);
              for (let c = 0; c < modulesW; c += 1) {
                const xCtr = ctrLocal[0] - halfW + modL_m / 2 + c * (modL_m + clamp_m);
                const panelLocal = [
                  [xCtr - modL_m / 2, yCtr - modW_m / 2],
                  [xCtr + modL_m / 2, yCtr - modW_m / 2],
                  [xCtr + modL_m / 2, yCtr + modW_m / 2],
                  [xCtr - modL_m / 2, yCtr + modW_m / 2],
                ];
                const panelLatLng = panelLocal.map((pt) => {
                  const ptEnu = rotatePoint(pt, ang);
                  const [lat, lon] = enuToLatLon(ptEnu[0], ptEnu[1], lat0, lon0);
                  return [lat, lon];
                });
                panels.push(panelLatLng);
              }
            }
            return panels;
          })
        : [];

    const lats = polygon.map(([lat]) => lat);
    const lngs = polygon.map(([, lng]) => lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    if (!isFinite(minLat) || !isFinite(minLng)) return null;

    const latPad = Math.max((maxLat - minLat) * 0.1, 0.0005);
    const lngPad = Math.max((maxLng - minLng) * 0.1, 0.0005);
    const baseBbox = [minLng - lngPad, minLat - latPad, maxLng + lngPad, maxLat + latPad];
    const baseLatSpan = baseBbox[3] - baseBbox[1];
    const baseLonSpan = baseBbox[2] - baseBbox[0];
    const centerLat = (baseBbox[1] + baseBbox[3]) / 2;
    const centerLon = (baseBbox[0] + baseBbox[2]) / 2;
    const approxWidthMeters = Math.max(
      baseLonSpan * 111320 * Math.max(Math.cos((centerLat * Math.PI) / 180), 0.01),
      1
    );
    const approxHeightMeters = Math.max(baseLatSpan * 110540, 1);
    const longestEdgeMeters = Math.max(approxWidthMeters, approxHeightMeters);
    const targetLongestPx = 1600;
    const minShortSidePx = 512;
    let widthPx = targetLongestPx;
    let heightPx = Math.max(
      minShortSidePx,
      Math.round((approxHeightMeters / longestEdgeMeters) * targetLongestPx)
    );
    if (approxHeightMeters > approxWidthMeters) {
      heightPx = targetLongestPx;
      widthPx = Math.max(
        minShortSidePx,
        Math.round((approxWidthMeters / longestEdgeMeters) * targetLongestPx)
      );
    }
    const cosLat = Math.max(Math.cos((centerLat * Math.PI) / 180), 0.15);
    const attemptMeters = [0.35, 0.6, 0.9, 1.5, 2.5, 4, 6];

    const attemptSnapshot = async (bbox, requestLatSpan, requestLonSpan) => {
      const requestLonRange = bbox[2] - bbox[0];
      const requestLatRange = bbox[3] - bbox[1];
      if (requestLonRange <= 0 || requestLatRange <= 0) {
        throw new Error("Invalid satellite bounds");
      }
      const url =
        "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export" +
        `?bbox=${bbox.join(",")}` +
        "&bboxSR=4326&imageSR=4326" +
        `&size=${widthPx},${heightPx}` +
        "&format=png32&f=image";

      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Satellite request failed (${resp.status})`);
      const contentType = resp.headers.get("content-type") || "";
      if (!/image|octet-stream/.test(contentType)) {
        const errorText = await resp.text();
        throw new Error(`Unexpected response: ${contentType} ${errorText}`);
      }
      const blob = await resp.blob();
      if (!blob || blob.size === 0) {
        throw new Error("Empty satellite response");
      }
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const baseImage = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas);
        };
        img.onerror = () => reject(new Error("Failed to decode satellite image"));
        img.src = base64;
      });

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = widthPx;
      finalCanvas.height = heightPx;
      const finalCtx = finalCanvas.getContext("2d");
      finalCtx.imageSmoothingEnabled = true;

      const spansDiffer =
        (requestLatSpan > baseLatSpan * 1.000001 || requestLonSpan > baseLonSpan * 1.000001) &&
        baseLatSpan > 0 &&
        baseLonSpan > 0;
      if (spansDiffer) {
        const cropX = ((baseBbox[0] - bbox[0]) / requestLonRange) * baseImage.width;
        const cropY = ((bbox[3] - baseBbox[3]) / requestLatRange) * baseImage.height;
        const cropWidth = (baseLonSpan / requestLonRange) * baseImage.width;
        const cropHeight = (baseLatSpan / requestLatRange) * baseImage.height;
        const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
        const safeCropX = clamp(cropX, 0, baseImage.width);
        const safeCropY = clamp(cropY, 0, baseImage.height);
        const safeCropWidth = clamp(cropWidth, 1, baseImage.width - safeCropX);
        const safeCropHeight = clamp(cropHeight, 1, baseImage.height - safeCropY);
        finalCtx.drawImage(
          baseImage,
          safeCropX,
          safeCropY,
          safeCropWidth,
          safeCropHeight,
          0,
          0,
          widthPx,
          heightPx
        );
      } else {
        finalCtx.drawImage(baseImage, 0, 0, widthPx, heightPx);
      }

      const projectBase = ([lat, lng]) => ({
        x: ((lng - baseBbox[0]) / baseLonSpan) * widthPx,
        y: heightPx - ((lat - baseBbox[1]) / baseLatSpan) * heightPx,
      });

      finalCtx.save();
      finalCtx.lineJoin = "round";
      finalCtx.lineCap = "round";

      if (polygon.length) {
        finalCtx.strokeStyle = "rgba(56,189,248,0.95)";
        finalCtx.lineWidth = Math.max(1.25, widthPx * 0.0026);
        finalCtx.beginPath();
        polygon.forEach((coord, idx) => {
          const { x, y } = projectBase(coord);
          if (idx === 0) finalCtx.moveTo(x, y);
          else finalCtx.lineTo(x, y);
        });
        finalCtx.closePath();
        finalCtx.stroke();
      }

      if (placementsLatLng.length) {
        const blockFill = "rgba(14,165,233,0.14)";
        finalCtx.strokeStyle = "rgba(14,165,233,0.94)";
        finalCtx.lineWidth = Math.max(1, widthPx * 0.0022);
        placementsLatLng.forEach((ring) => {
          if (!ring.length) return;
          finalCtx.beginPath();
          ring.forEach((coord, idx) => {
            const { x, y } = projectBase(coord);
            if (idx === 0) finalCtx.moveTo(x, y);
            else finalCtx.lineTo(x, y);
          });
          finalCtx.closePath();
          finalCtx.fillStyle = blockFill;
          finalCtx.fill();
          finalCtx.stroke();
        });
      }

      if (panelFootprintsLatLng.length) {
        finalCtx.strokeStyle = "rgba(15,23,42,0.7)";
        finalCtx.lineWidth = Math.max(0.5, widthPx * 0.0011);
        panelFootprintsLatLng.forEach((panels) => {
          panels.forEach((panel) => {
            if (!panel.length) return;
            finalCtx.beginPath();
            panel.forEach((coord, idx) => {
              const { x, y } = projectBase(coord);
              if (idx === 0) finalCtx.moveTo(x, y);
              else finalCtx.lineTo(x, y);
            });
            finalCtx.closePath();
            finalCtx.stroke();
          });
        });
      }

      finalCtx.restore();

      return {
        dataUrl: finalCanvas.toDataURL("image/png"),
        canvasWidth: finalCanvas.width || widthPx,
        canvasHeight: finalCanvas.height || heightPx,
      };
    };

    for (let i = 0; i < attemptMeters.length; i += 1) {
      const metersPerPx = attemptMeters[i];
      const minLatSpanDeg = (metersPerPx * heightPx) / 110540;
      const minLonSpanDeg = (metersPerPx * widthPx) / (111320 * cosLat);
      const requestLatSpan = Math.max(baseLatSpan, minLatSpanDeg);
      const requestLonSpan = Math.max(baseLonSpan, minLonSpanDeg);
      const bbox = [
        centerLon - requestLonSpan / 2,
        centerLat - requestLatSpan / 2,
        centerLon + requestLonSpan / 2,
        centerLat + requestLatSpan / 2,
      ];
      try {
        const snapshot = await attemptSnapshot(bbox, requestLatSpan, requestLonSpan);
        if (snapshot) {
          if (i > 0) {
            console.info(
              `Satellite imagery fetched at ${metersPerPx.toFixed(2)} m/px after ${i} retries.`
            );
          }
          return snapshot;
        }
      } catch (err) {
        console.warn(`Satellite imagery attempt ${i + 1} failed`, err);
      }
    }

    console.warn("Failed to load satellite imagery after fallback attempts");
    return null;
  }

  async function downloadPdf() {
    if (isExportingPdf) return;
    if (polygon.length === 0) {
      alert("Add at least one site vertex to export a PDF.");
      return;
    }
    setIsExportingPdf(true);
    try {
      const niceNumber = (val) => {
        if (!isFinite(val) || val <= 0) return 1;
        const base = 10 ** Math.floor(Math.log10(val));
        const candidates = [1, 2, 2.5, 5, 10];
        const factor = val / base;
        const candidate = candidates.find((c) => c >= factor) || 10;
        return candidate * base;
      };

      const [sketch, satellite] = await Promise.all([
        Promise.resolve(buildLayoutSketch()),
        fetchSatelliteSnapshot(),
      ]);

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const border = 12;
      const innerWidth = pageWidth - border * 2;
      const innerHeight = pageHeight - border * 2;
      const titleBlockWidth = 96;
      const contentGap = 6;
      const contentX = border + contentGap;
      const contentWidth = innerWidth - titleBlockWidth - contentGap * 2;
      const titleX = border + innerWidth - titleBlockWidth;

      const now = new Date();
      const presetInfo = preset;

      doc.setDrawColor(15, 23, 42);
      doc.setLineWidth(0.35);
      doc.rect(border, border, innerWidth, innerHeight);
      doc.line(titleX, border, titleX, border + innerHeight);

      const formatCoord = (value, posLabel, negLabel) =>
        `${formatNumber(Math.abs(value), 5)}° ${value >= 0 ? posLabel : negLabel}`;

      const buildTitleSections = () => {
        const sections = [
          { label: "PROJECT", lines: [projectName || "PEG Layout"] },
          {
            label: "LOCATION",
            lines: polyCenter
              ? [
                  formatCoord(polyCenter[0], "N", "S"),
                  formatCoord(polyCenter[1], "E", "W"),
                ]
              : ["Coordinates unavailable"],
          },
          {
            label: "SYSTEM",
            lines: [`Type: ${systemType.toUpperCase()}`, `Module: ${presetInfo.label}`],
          },
          {
            label: "CAPACITY",
            lines: [
              `Blocks: ${metrics.tables.toLocaleString()}`,
              `Modules: ${metrics.modules.toLocaleString()}`,
              `DC: ${formatNumber(metrics.kWp, 1)} kWp`,
              `Prod.: ${formatNumber(metrics.annualMWh, 1)} MWh/yr`,
            ],
          },
          {
            label: "PERFORMANCE",
            lines: [
              `Specific yield: ${formatNumber(specificYield, 0)} kWh/kWp`,
              `GCR: ${formatNumber(metrics.gcr * 100, 1)} %`,
            ],
          },
          {
            label: "FINANCIAL",
            lines: [
              `CAPEX: ${currency} ${formatNumber(totalCapex, 0)}`,
              `Cost/kWp: ${currency} ${formatNumber(costPerKwp, 0)}`,
              `Cost/kWh·yr: ${currency} ${formatNumber(costPerKwhYear, 2)}`,
              `LCOE: ${currency} ${formatNumber(lcoe, 4)}/kWh`,
            ],
          },
        ];
        return sections;
      };

      const renderTitleBlock = (sections) => {
        let cursor = border;
        const signatureHeight = 22;
        const bottomLimit = border + innerHeight - signatureHeight - 2;
        const sectionWidth = titleBlockWidth - 6;
        doc.setLineWidth(0.2);
        sections.forEach((section) => {
          if (!section || cursor >= bottomLimit) return;
          const bodyLines = (section.lines || [])
            .filter(Boolean)
            .flatMap((line) => doc.splitTextToSize(line, sectionWidth));
          const height = Math.max(12, bodyLines.length * 3.8 + 7);
          const sectionBottom = Math.min(cursor + height, bottomLimit);
          doc.line(titleX, cursor, titleX + titleBlockWidth, cursor);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8.5);
          doc.text(section.label, titleX + 3, cursor + 4.5);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.4);
          let lineY = cursor + 8;
          bodyLines.forEach((line) => {
            if (lineY > sectionBottom - 1.5) return;
            doc.text(line, titleX + 3, lineY);
            lineY += 3.6;
          });
          cursor = sectionBottom;
        });
        doc.line(titleX, cursor, titleX + titleBlockWidth, cursor);
        const signatureTop = border + innerHeight - signatureHeight;
        doc.line(titleX, signatureTop, titleX + titleBlockWidth, signatureTop);
        doc.line(
          titleX + titleBlockWidth / 2,
          signatureTop,
          titleX + titleBlockWidth / 2,
          border + innerHeight
        );
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("DRAWN", titleX + 3, signatureTop + 4);
        doc.text("CHECKED", titleX + titleBlockWidth / 2 + 3, signatureTop + 4);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.4);
        doc.text("PEG Layouts", titleX + 3, signatureTop + 9);
        doc.text(now.toLocaleDateString(), titleX + titleBlockWidth / 2 + 3, signatureTop + 9);
      };

      const drawHeader = (subtitle) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        let cursor = border + 14;
        const titleLines = doc.splitTextToSize(projectName || "PEG Layout", contentWidth - 10);
        titleLines.forEach((line) => {
          doc.text(line, contentX, cursor);
          cursor += 6.4;
        });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        const subtitleLines = doc.splitTextToSize(subtitle, contentWidth - 10);
        subtitleLines.forEach((line) => {
          doc.text(line, contentX, cursor);
          cursor += 5.6;
        });
        doc.setFontSize(8.5);
        doc.text(`Generated: ${now.toLocaleString()}`, contentX, cursor + 2.8);
        doc.text("Datum: WGS84", contentX, cursor + 6.8);
        cursor += 12.5;
        doc.line(contentX, cursor, contentX + contentWidth, cursor);
        return cursor;
      };

      const planHeaderBottom = drawHeader("GENERAL ARRANGEMENT – PLAN");

      const planFrameX = contentX;
      const planFrameY = planHeaderBottom + 6;
      let planFrameHeight = innerHeight - (planFrameY - border) - 72;
      if (planFrameHeight < 150) {
        planFrameHeight = innerHeight - (planFrameY - border) - 48;
      }
      const planFrameWidth = contentWidth;
      const planBottomMargin = 6;
      const legendAreaHeight = 34;
      let planHeight = planFrameHeight - legendAreaHeight - planBottomMargin;
      if (planHeight < 120) {
        planHeight = planFrameHeight - 40;
      }

      doc.setLineWidth(0.25);
      doc.rect(planFrameX, planFrameY, planFrameWidth, planHeight);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("SITE LAYOUT", planFrameX, planFrameY - 2, { baseline: "bottom" });

      let planScaleText = "Not to scale";
      if (sketch) {
        const padding = 10;
        const availableWidth = planFrameWidth - padding * 2;
        const availableHeight = planHeight - padding * 2;
        let imgWidth = availableWidth;
        let imgHeight = (imgWidth * sketch.canvasHeight) / sketch.canvasWidth;
        if (imgHeight > availableHeight) {
          imgHeight = availableHeight;
          imgWidth = (imgHeight * sketch.canvasWidth) / sketch.canvasHeight;
        }
        const imgX = planFrameX + padding + (availableWidth - imgWidth) / 2;
        const imgY = planFrameY + padding + (availableHeight - imgHeight) / 2;
        doc.addImage(sketch.dataUrl, "PNG", imgX, imgY, imgWidth, imgHeight);

        const rawScale = (sketch.bounds.width * 1000) / imgWidth;
        const scaleDenom = Math.max(1, Math.round(niceNumber(rawScale)));
        planScaleText = `Scale 1:${formatNumber(scaleDenom, 0)}`;
        const metresPerMm = scaleDenom / 1000;
        let barMeters = niceNumber(sketch.bounds.width / 5);
        let barWidth = barMeters / metresPerMm;
        const maxBarWidth = planFrameWidth * 0.45;
        if (barWidth > maxBarWidth) {
          const scale = maxBarWidth / barWidth;
          barWidth *= scale;
          barMeters *= scale;
        }
        const barX = planFrameX + 10;
        const barY = planFrameY + planHeight - 12;
        const segments = 4;
        const segmentWidth = barWidth / segments;
        doc.setLineWidth(0.35);
        for (let i = 0; i < segments; i++) {
          if (i % 2 === 0) doc.setFillColor(30, 41, 59);
          else doc.setFillColor(255, 255, 255);
          doc.rect(barX + i * segmentWidth, barY, segmentWidth, 3.4, "F");
          doc.rect(barX + i * segmentWidth, barY, segmentWidth, 3.4);
        }
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.text(planScaleText, barX, barY - 2.2);
        doc.setFont("helvetica", "normal");
        doc.text("0", barX, barY + 6.2);
        doc.text(
          `${formatNumber(barMeters, barMeters >= 1000 ? 0 : 1)} m`,
          barX + barWidth,
          barY + 6.2,
          { align: "right" }
        );

        const drawEdgeDimensions = () => {
          const outlineCanvas = Array.isArray(sketch.outlineCanvas)
            ? sketch.outlineCanvas
            : null;
          const outlineEnu = Array.isArray(sketch.outlineEnu) ? sketch.outlineEnu : null;
          if (!outlineCanvas || !outlineEnu) return;
          const count = Math.min(outlineCanvas.length, outlineEnu.length);
          if (count < 2) return;
          const canvasPts = [];
          const enuPts = [];
          for (let i = 0; i < count; i += 1) {
            const canvasPt = outlineCanvas[i];
            const enuPt = outlineEnu[i];
            if (
              !canvasPt ||
              !enuPt ||
              !isFinite(canvasPt.x) ||
              !isFinite(canvasPt.y) ||
              !Array.isArray(enuPt) ||
              !isFinite(enuPt[0]) ||
              !isFinite(enuPt[1])
            ) {
              return;
            }
            canvasPts.push({ x: canvasPt.x, y: canvasPt.y });
            enuPts.push([enuPt[0], enuPt[1]]);
          }
          if (canvasPts.length > 1) {
            const firstCanvas = canvasPts[0];
            const lastCanvas = canvasPts[canvasPts.length - 1];
            const firstEnu = enuPts[0];
            const lastEnu = enuPts[enuPts.length - 1];
            const sameCanvas =
              Math.abs(firstCanvas.x - lastCanvas.x) < 1e-3 &&
              Math.abs(firstCanvas.y - lastCanvas.y) < 1e-3;
            const sameEnu = Math.hypot(firstEnu[0] - lastEnu[0], firstEnu[1] - lastEnu[1]) < 1e-3;
            if (sameCanvas && sameEnu) {
              canvasPts.pop();
              enuPts.pop();
            }
          }
          if (canvasPts.length < 2) return;

          let signedAreaEnu = 0;
          for (let i = 0; i < enuPts.length; i += 1) {
            const [x1, y1] = enuPts[i];
            const [x2, y2] = enuPts[(i + 1) % enuPts.length];
            signedAreaEnu += x1 * y2 - x2 * y1;
          }
          const preferRightNormal = signedAreaEnu > 0;
          const canvasToPdfX = imgWidth / sketch.canvasWidth;
          const canvasToPdfY = imgHeight / sketch.canvasHeight;
          const toPdf = (pt) => ({
            x: imgX + pt.x * canvasToPdfX,
            y: imgY + pt.y * canvasToPdfY,
          });
          const fromPdf = (pt) => ({
            x: (pt.x - imgX) / canvasToPdfX,
            y: (pt.y - imgY) / canvasToPdfY,
          });
          const baseOffset = Math.max(8, Math.min(22, Math.min(imgWidth, imgHeight) * 0.075));
          const arrowLength = 3.2;
          const arrowAngle = Math.PI / 6;
          doc.setLineWidth(0.2);

          const canvasPoly = canvasPts.map((pt) => [pt.x, pt.y]);

          const buildDimPoints = (normalVec, startPdf, endPdf, offset) => ({
            dimStart: {
              x: startPdf.x + normalVec.x * offset,
              y: startPdf.y + normalVec.y * offset,
            },
            dimEnd: {
              x: endPdf.x + normalVec.x * offset,
              y: endPdf.y + normalVec.y * offset,
            },
          });

          for (let i = 0; i < canvasPts.length; i += 1) {
            const startCanvas = canvasPts[i];
            const endCanvas = canvasPts[(i + 1) % canvasPts.length];
            const startEnu = enuPts[i];
            const endEnu = enuPts[(i + 1) % enuPts.length];
            const lengthMeters = Math.hypot(endEnu[0] - startEnu[0], endEnu[1] - startEnu[1]);
            if (!isFinite(lengthMeters) || lengthMeters < 0.5) continue;

            const startPdf = toPdf(startCanvas);
            const endPdf = toPdf(endCanvas);
            const vx = endPdf.x - startPdf.x;
            const vy = endPdf.y - startPdf.y;
            const edgeLenPdf = Math.hypot(vx, vy);
            if (!isFinite(edgeLenPdf) || edgeLenPdf < 0.5) continue;

            const tangent = { x: vx / edgeLenPdf, y: vy / edgeLenPdf };
            const rightNormal = { x: tangent.y, y: -tangent.x };
            const leftNormal = { x: -tangent.y, y: tangent.x };
            let normal = preferRightNormal ? rightNormal : leftNormal;
            const offset = Math.min(baseOffset, edgeLenPdf / 2.4);

            let { dimStart, dimEnd } = buildDimPoints(normal, startPdf, endPdf, offset);
            const dimMidPdf = {
              x: (dimStart.x + dimEnd.x) / 2,
              y: (dimStart.y + dimEnd.y) / 2,
            };
            const dimMidCanvas = fromPdf(dimMidPdf);
            if (pointInPoly([dimMidCanvas.x, dimMidCanvas.y], canvasPoly)) {
              normal = { x: -normal.x, y: -normal.y };
              ({ dimStart, dimEnd } = buildDimPoints(normal, startPdf, endPdf, offset));
            }

            doc.line(startPdf.x, startPdf.y, dimStart.x, dimStart.y);
            doc.line(endPdf.x, endPdf.y, dimEnd.x, dimEnd.y);
            doc.line(dimStart.x, dimStart.y, dimEnd.x, dimEnd.y);

            const drawArrow = (x, y, dirX, dirY) => {
              const cos = Math.cos(arrowAngle);
              const sin = Math.sin(arrowAngle);
              const ax1 = dirX * cos + normal.x * sin;
              const ay1 = dirY * cos + normal.y * sin;
              const ax2 = dirX * cos - normal.x * sin;
              const ay2 = dirY * cos - normal.y * sin;
              doc.line(x, y, x + ax1 * arrowLength, y + ay1 * arrowLength);
              doc.line(x, y, x + ax2 * arrowLength, y + ay2 * arrowLength);
            };

            drawArrow(dimStart.x, dimStart.y, tangent.x, tangent.y);
            drawArrow(dimEnd.x, dimEnd.y, -tangent.x, -tangent.y);

            const decimals =
              lengthMeters >= 200 ? 0 : lengthMeters >= 20 ? 1 : lengthMeters >= 2 ? 2 : 3;
            const label = `${formatNumber(lengthMeters, decimals)} m`;
            const labelX = (dimStart.x + dimEnd.x) / 2 + normal.x * 2.4;
            const labelY = (dimStart.y + dimEnd.y) / 2 + normal.y * 2.4;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.text(label, labelX, labelY, {
              align: "center",
              angle: (Math.atan2(vy, vx) * 180) / Math.PI,
            });
            doc.setFont("helvetica", "normal");
          }
        };

        drawEdgeDimensions();

        doc.setFontSize(7.5);
        doc.setFont("helvetica", "normal");
        doc.text(
          `Extent: ${formatNumber(sketch.bounds.width, 1)} m × ${formatNumber(
            sketch.bounds.height,
            1
          )} m`,
          planFrameX + planFrameWidth - 4,
          planFrameY + planHeight - 12,
          { align: "right" }
        );
        doc.text(
          `Array orientation: ${formatNumber(orientationDeg, 1)}°`,
          planFrameX + planFrameWidth - 4,
          planFrameY + planHeight - 6,
          { align: "right" }
        );
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text("Layout drawing unavailable", planFrameX + 6, planFrameY + 12);
      }

      const legendTop = planFrameY + planHeight + 6;
      const legendWidth = planFrameWidth * 0.45;
      const metricsX = planFrameX + legendWidth + 8;
      const metricsWidth = planFrameWidth - legendWidth - 8;
      const bottomHeight = border + innerHeight - legendTop - 6;

      doc.setFillColor(248, 250, 252);
      doc.rect(planFrameX, legendTop, legendWidth, bottomHeight, "F");
      doc.rect(planFrameX, legendTop, legendWidth, bottomHeight);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("LEGEND", planFrameX + 3, legendTop + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const legendRow = (y, draw) => {
        draw(y);
      };
      legendRow(legendTop + 14, (y) => {
        doc.setFillColor(220, 38, 38);
        doc.rect(planFrameX + 4, y - 4, 7, 7, "F");
        doc.rect(planFrameX + 4, y - 4, 7, 7);
        doc.text("PV Block footprint", planFrameX + 14, y);
      });
      legendRow(legendTop + 26, (y) => {
        doc.setFillColor(191, 219, 254);
        doc.rect(planFrameX + 4, y - 4, 7, 7, "F");
        doc.setDrawColor(14, 165, 233);
        doc.rect(planFrameX + 4, y - 4, 7, 7);
        doc.setDrawColor(15, 23, 42);
        doc.text("Site boundary", planFrameX + 14, y);
      });
      legendRow(legendTop + 38, (y) => {
        doc.setDrawColor(51, 65, 85);
        doc.setLineWidth(0.4);
        doc.line(planFrameX + 4, y, planFrameX + 11, y);
        doc.setLineWidth(0.25);
        doc.setDrawColor(15, 23, 42);
        doc.text("Dimension reference", planFrameX + 14, y + 0.4);
      });
      doc.setFontSize(7.4);
      const notes = doc.splitTextToSize(
        "Dimensions measured to block extents. Verify on site before construction. Imagery and yield are indicative.",
        legendWidth - 8
      );
      doc.text(notes, planFrameX + 4, legendTop + bottomHeight - 8);

      doc.setLineWidth(0.25);
      doc.rect(metricsX, legendTop, metricsWidth, bottomHeight);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("PROJECT METRICS", metricsX + 3, legendTop + 6);
      doc.line(metricsX, legendTop + 8, metricsX + metricsWidth, legendTop + 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const metricsRows = [
        ["Blocks", metrics.tables.toLocaleString()],
        ["Modules", metrics.modules.toLocaleString()],
        ["DC capacity", `${formatNumber(metrics.kWp, 1)} kWp`],
        ["Specific yield", `${formatNumber(specificYield, 0)} kWh/kWp`],
        ["Annual production", `${formatNumber(metrics.annualMWh, 1)} MWh`],
        ["Ground coverage", `${formatNumber(metrics.gcr * 100, 1)} %`],
        ["Site area", `${formatNumber(metrics.siteArea_m2 / 10000, 2)} ha`],
        ["Cost/kWp", `${currency} ${formatNumber(costPerKwp, 0)}`],
        ["LCOE", `${currency} ${formatNumber(lcoe, 4)}/kWh`],
      ];
      const rowHeight = 6.6;
      let rowY = legendTop + 13;
      metricsRows.forEach(([label, value]) => {
        if (rowY > legendTop + bottomHeight - 6) return;
        doc.text(label, metricsX + 3, rowY);
        doc.text(value, metricsX + metricsWidth - 3, rowY, { align: "right" });
        rowY += rowHeight;
        doc.setDrawColor(226, 232, 240);
        doc.line(metricsX, rowY - 2.4, metricsX + metricsWidth, rowY - 2.4);
        doc.setDrawColor(15, 23, 42);
      });
      doc.setFontSize(7.2);
      doc.text(planScaleText, metricsX + metricsWidth - 3, legendTop + bottomHeight - 6, {
        align: "right",
      });

      const planSections = [...buildTitleSections(), { label: "SHEET", lines: ["GA-01", "General Arrangement", "Sheet 1 of 2"] }];
      renderTitleBlock(planSections);

      doc.addPage({ orientation: "landscape", unit: "mm", format: "a3" });
      doc.setDrawColor(15, 23, 42);
      doc.setLineWidth(0.35);
      doc.rect(border, border, innerWidth, innerHeight);
      doc.line(titleX, border, titleX, border + innerHeight);

      const satelliteHeaderBottom = drawHeader("SATELLITE CONTEXT – PANEL FOOTPRINT");
      const mapFrameX = contentX;
      const mapFrameY = satelliteHeaderBottom + 6;
      const mapFrameWidth = contentWidth;
      const mapFrameHeight = innerHeight - (mapFrameY - border) - 48;

      doc.setLineWidth(0.25);
      doc.rect(mapFrameX, mapFrameY, mapFrameWidth, mapFrameHeight);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("SATELLITE OVERLAY", mapFrameX, mapFrameY - 2, { baseline: "bottom" });

      if (satellite) {
        const padding = 10;
        const availableWidth = mapFrameWidth - padding * 2;
        const availableHeight = mapFrameHeight - padding * 2;
        let imgWidth = availableWidth;
        let imgHeight = (imgWidth * satellite.canvasHeight) / satellite.canvasWidth;
        if (imgHeight > availableHeight) {
          imgHeight = availableHeight;
          imgWidth = (imgHeight * satellite.canvasWidth) / satellite.canvasHeight;
        }
        const imgX = mapFrameX + padding + (availableWidth - imgWidth) / 2;
        const imgY = mapFrameY + padding + (availableHeight - imgHeight) / 2;
        doc.addImage(satellite.dataUrl, "PNG", imgX, imgY, imgWidth, imgHeight);
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text("Satellite imagery unavailable", mapFrameX + 6, mapFrameY + 12);
      }

      const contextTop = mapFrameY + mapFrameHeight + 6;
      const contextHeight = border + innerHeight - contextTop - 6;
      doc.setFillColor(248, 250, 252);
      doc.rect(mapFrameX, contextTop, mapFrameWidth, contextHeight, "F");
      doc.rect(mapFrameX, contextTop, mapFrameWidth, contextHeight);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text("NOTES", mapFrameX + 3, contextTop + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.6);
      const contextNotes = doc.splitTextToSize(
        "Overlay depicts PV block footprints on Esri World Imagery basemap. Review site constraints, topography, and access prior to detailed design.",
        mapFrameWidth - 8
      );
      doc.text(contextNotes, mapFrameX + 3, contextTop + 12);
      if (!satellite) {
        const fallbackNotes = doc.splitTextToSize(
          "Satellite imagery could not be retrieved automatically. Please ensure connectivity and retry the export if imagery is required.",
          mapFrameWidth - 8
        );
        doc.text(fallbackNotes, mapFrameX + 3, contextTop + 24);
      }

      const satelliteSections = [
        ...buildTitleSections(),
        { label: "SHEET", lines: ["GA-02", "Satellite Context", "Sheet 2 of 2"] },
      ];
      renderTitleBlock(satelliteSections);

      const safeName = projectName || "project";
      doc.save(`${safeName.replace(/[^a-z0-9-_]+/gi, "_")}_layout.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      alert("Unable to generate the PDF at this time. Please try again.");
    } finally {
      setIsExportingPdf(false);
    }
  }

  function onProjectFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        applyProject(JSON.parse(ev.target.result));
      } catch (err) {
        console.error("Invalid project file", err);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  // ---- Map init ----
  useEffect(() => {
    const map = L.map(mapElRef.current, {
      center: DEFAULT_CENTER,
      zoom: 18,
      maxZoom: 22,
      zoomControl: true,
      zoomSnap: 0.1,
      scrollWheelZoom: false
    });
    mapRef.current = map;

    const container = map.getContainer();
    function onWheel(e) {
      e.preventDefault();
      e.stopPropagation();
      const step = e.ctrlKey ? 0.1 : 1;
      const delta = e.deltaY < 0 ? step : -step;
      const latlng = map.mouseEventToLatLng(e);
      map.setZoomAround(latlng, map.getZoom() + delta);
    }
    container.addEventListener("wheel", onWheel, { passive: false, capture: true });

    // Base layers
    const osm = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 22,
      maxNativeZoom: 19
    });

    const esriWorldImagery = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles &copy; Esri", maxZoom: 22, maxNativeZoom: 17 }
    );

    // Default to satellite
    esriWorldImagery.addTo(map);

    // Layer control (bottom-right)
    L.control.layers(
      { Satellite: esriWorldImagery, Map: osm },
      null,
      { position: "bottomright", collapsed: true }
    ).addTo(map);

    // Layers
    polygonLayerRef.current = L.polygon(polygon, {
      color: "#2a5bd7", weight: 2, fillColor: "#2a5bd7", fillOpacity: 0.2
    }).addTo(map);

    const renderer = L.canvas({ padding: 0.3 });
    tablesLayerRef.current = L.layerGroup([], { renderer }).addTo(map);
    moduleLayerRef.current = L.layerGroup([], { renderer }).addTo(map);
    blockLabelLayerRef.current = L.layerGroup([]).addTo(map);
    labelLayerRef.current = L.layerGroup([]).addTo(map);
    bufferLayerRef.current = L.layerGroup([]).addTo(map);
    outlineDimLayerRef.current = L.layerGroup([]).addTo(map);

    if (autoFit && polygon.length >= 1) {
      const b = polygonLayerRef.current.getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [20, 20] });
    }

    // Add-point click behavior
    function onMapClick(e) {
      if (!addModeRef.current) return;
      // Add a new vertex at click
      setPolygon((poly) => {
        // append at end
        const updated = [...poly, [round6(e.latlng.lat), round6(e.latlng.lng)]];
        return updated;
      });
    }
    function onZoom() {
      setZoom(map.getZoom ? map.getZoom() : 0);
      redrawTablesRef.current();
    }

    map.on("click", onMapClick);
    map.on("zoomend", onZoom);
    setZoom(map.getZoom ? map.getZoom() : 0);

    rebuildHandles(polygon);
    rebuildLabels(polygon);

    return () => {
      handleMarkersRef.current.forEach((m) => m.remove());
      map.off("click", onMapClick);
      map.off("zoomend", onZoom);
      container.removeEventListener("wheel", onWheel, { capture: true });
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure the map resizes correctly when returning to the design page
  useEffect(() => {
    if (page === "design" && mapRef.current) {
      // Defer to next tick so the element is visible
      setTimeout(() => mapRef.current.invalidateSize(), 0);
    }
  }, [page]);

  // Update polygon, handles, labels, tables when inputs change
  useEffect(() => {
    if (!polygonLayerRef.current || !mapRef.current) return;

    // If fewer than 3 points, render as a thin polyline to show location
    if (polygon.length >= 3) {
      polygonLayerRef.current.setLatLngs(polygon);
      polygonLayerRef.current.setStyle({ fillOpacity: 0.2, weight: 2 });
    } else {
      polygonLayerRef.current.setLatLngs(polygon);
      polygonLayerRef.current.setStyle({ fillOpacity: 0.0, weight: 2 });
    }

    if (autoFit && polygon.length >= 1) {
      const b = polygonLayerRef.current.getBounds();
      if (b.isValid()) mapRef.current.fitBounds(b, { padding: [20, 20] });
    }

    rebuildHandles(polygon);
    rebuildLabels(polygon);
    redrawTables(); // recompute placements & metrics
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    polygon,
    autoFit,
    presetId,
    landscape,
    modulesW,
    modulesH,
    clampGapMm,
    gapNS,
    gapEW,
    bufferM,
    orientationDeg,
    alignGrid,
    specificYield,
    systemType,
  ]);

  /* ---------- Table Placement Engine ---------- */
  function redrawTables(overrideFillBlocks, overrideOrigin, overridePolygon) {
    const map = mapRef.current;
    if (
      !map ||
      !tablesLayerRef.current ||
      !moduleLayerRef.current ||
      !blockLabelLayerRef.current ||
      !bufferLayerRef.current ||
      !outlineDimLayerRef.current
    )
      return;
    tablesLayerRef.current.clearLayers();
    moduleLayerRef.current.clearLayers();
    blockLabelLayerRef.current.clearLayers();
    bufferLayerRef.current.clearLayers();
    outlineDimLayerRef.current.clearLayers();
    placementsRef.current = [];

    const polyBase = overridePolygon ?? polygon;
    let poly = polyBase;
    let bufferPoly = null;

      if (bufferM > 0 && polyBase.length >= 3) {
        try {
          const ringBase = closeRing(polyBase.map(([lat, lng]) => [lng, lat]));
          const tPoly = turfPolygon([ringBase]);
          const buff = buffer(tPoly, -bufferM, { units: "meters" });
          let coords;
          if (buff && buff.geometry) {
            if (buff.geometry.type === "Polygon")
              coords = buff.geometry.coordinates[0];
            else if (buff.geometry.type === "MultiPolygon")
              coords = buff.geometry.coordinates[0][0];
          }
          if (coords && coords.length >= 4) {
            const inner = coords.slice(0, -1);
            poly = inner.map(([lng, lat]) => [lat, lng]);
            bufferPoly = poly;
            const ring = [...poly, poly[0]];
            L.polygon(ring, {
              color: "#f97316",
              weight: 1,
              fillOpacity: 0,
              interactive: false
            }).addTo(bufferLayerRef.current);
          }
        } catch (err) {
          console.error("buffer failed", err);
        }
      }

    const zoom = map.getZoom ? map.getZoom() : 0;
    const addDimLabels = (pts, cls) => {
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        const dist = map.distance(L.latLng(a[0], a[1]), L.latLng(b[0], b[1]));
        const icon = L.divIcon({
          className: `dim-label ${cls}`,
          html: `${formatNumber(dist, 1)} m`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        });
        L.marker(mid, { icon, interactive: false }).addTo(outlineDimLayerRef.current);
      }
    };
    if (zoom >= 16) {
      if (polyBase.length >= 2) addDimLabels(polyBase, "main");
      if (bufferPoly && bufferPoly.length >= 2) addDimLabels(bufferPoly, "buffer");
    }

    if (poly.length < 3) {
      setMetrics((m) => ({ ...m, tables: 0, modules: 0, kWp: 0, annualMWh: 0, gcr: 0, kWpPerHa: 0, siteArea_m2: 0 }));
      return;
    }
    const showModules = map.getZoom && map.getZoom() >= PANEL_ZOOM;
    const showLabels = map.getZoom && map.getZoom() >= LABEL_ZOOM;
    const modulesPerTable = modulesW * modulesH;
    const blockPower = formatPower((modulesPerTable * preset.P_w) / 1000);

    // 1) centroid (area-weighted)
    const [lon0, lat0] = polygonCentroid(poly.map(([lat, lon]) => [lon, lat]));

    // 2) polygon in ENU
    const polyENU = poly.map(([lat, lng]) => latLonToEnu(lat, lng, lat0, lon0));

    // 3) rotate to local (rows horizontal)
    const ang = radians(orientationDeg);
    const polyLocal = polyENU.map((p) => rotatePoint(p, -ang));

    // 4) bounds
    const b = polygonBounds(polyLocal);
    const bEnu = polygonBounds(polyENU); // preserve map orientation for origins
    if (
      !(
        isFinite(b.minX) &&
        isFinite(b.minY) &&
        isFinite(b.maxX) &&
        isFinite(b.maxY)
      )
    )
      return;

    // pitches from gaps
    const rowStep = Math.max(rowPitchM, 0.1);
    const colStep = Math.max(columnPitchM, tableW_m, 0.1);

    const halfW = tableW_m / 2;
    const halfH = tableH_m / 2;

    // 5) fill grid
    let placements = [];
    const xOffsets = alignGrid
      ? [0]
      : Array.from({ length: 10 }, (_, i) => (colStep * i) / 10);
    const yOffsets = alignGrid
      ? [0]
      : Array.from({ length: 10 }, (_, i) => (rowStep * i) / 10);
    for (const yOff of yOffsets) {
      const current = [];
      for (let y = b.minY + halfH + yOff; y <= b.maxY - halfH; y += rowStep) {
        let bestRow = [];
        for (const xOff of xOffsets) {
          const row = [];
          for (let x = b.minX + halfW + xOff; x <= b.maxX - halfW; x += colStep) {
            const rectLocal = [
              [x - halfW, y - halfH],
              [x + halfW, y - halfH],
              [x + halfW, y + halfH],
              [x - halfW, y + halfH],
            ];
            const ctr = [x, y];
            if (
              pointInPoly(ctr, polyLocal) &&
              rectLocal.every((pt) => pointInPoly(pt, polyLocal))
            ) {
              row.push(rectLocal);
            }
          }
          if (row.length > bestRow.length) bestRow = row;
        }
        current.push(...bestRow);
      }
      if (current.length > placements.length) placements = current;
    }
    // sort placements based on fill origin (map-aligned)
    const origin = overrideOrigin ?? fillOrigin;
    let anchorEnu;
    switch (origin) {
      case "N":
        anchorEnu = [(bEnu.minX + bEnu.maxX) / 2, bEnu.maxY];
        break;
      case "NE":
        anchorEnu = [bEnu.maxX, bEnu.maxY];
        break;
      case "E":
        anchorEnu = [bEnu.maxX, (bEnu.minY + bEnu.maxY) / 2];
        break;
      case "SE":
        anchorEnu = [bEnu.maxX, bEnu.minY];
        break;
      case "S":
        anchorEnu = [(bEnu.minX + bEnu.maxX) / 2, bEnu.minY];
        break;
      case "SW":
        anchorEnu = [bEnu.minX, bEnu.minY];
        break;
      case "W":
        anchorEnu = [bEnu.minX, (bEnu.minY + bEnu.maxY) / 2];
        break;
      case "NW":
      default:
        anchorEnu = [bEnu.minX, bEnu.maxY];
        break;
    }
    const anchor = rotatePoint(anchorEnu, -ang);
    const placementsSorted = placements
      .map((rectLocal) => {
        const ctr = [
          (rectLocal[0][0] + rectLocal[2][0]) / 2,
          (rectLocal[0][1] + rectLocal[2][1]) / 2,
        ];
        const d = (ctr[0] - anchor[0]) ** 2 + (ctr[1] - anchor[1]) ** 2;
        return { rectLocal, d };
      })
      .sort((a, b) => a.d - b.d)
      .map((p) => p.rectLocal);
    const total = placementsSorted.length;
    const prevMaxFill = maxFillBlocks;
    setMaxFillBlocks(total);
    let limit = overrideFillBlocks ?? (fillAll ? total : fillBlocks);
    if (
      limit <= 0 ||
      limit > total ||
      (!fillAll && fillBlocks === prevMaxFill)
    ) {
      limit = total;
      if (!fillAll && fillBlocks !== total) setFillBlocks(total);
    }
    const finalPlacements = placementsSorted.slice(0, limit);
    placementsRef.current = finalPlacements;

    // 6) draw tables + metrics
    finalPlacements.forEach((rectLocal, idx) => {
      const ringLatLng = rectLocal.map((pt) => {
        const ptENU = rotatePoint(pt, ang);
        const [lat, lon] = enuToLatLon(ptENU[0], ptENU[1], lat0, lon0);
        return [lat, lon];
      });
      ringLatLng.push(ringLatLng[0]);
      L.polygon(ringLatLng, {
        color: "#555",
        weight: 1,
        fillColor: "#000",
        fillOpacity: 0.35,
        interactive: false,
        pane: "overlayPane",
      }).addTo(tablesLayerRef.current);

      // block label
      const ctrLocal = [
        (rectLocal[0][0] + rectLocal[2][0]) / 2,
        (rectLocal[0][1] + rectLocal[2][1]) / 2,
      ];
      const ctrENU = rotatePoint(ctrLocal, ang);
      const [cLat, cLon] = enuToLatLon(ctrENU[0], ctrENU[1], lat0, lon0);
      if (showLabels) {
        const labelIcon = L.divIcon({
          className: "block-label",
          html: `${idx + 1}<br>${blockPower.value} ${blockPower.unit}p`,
          iconSize: [60, 30],
          iconAnchor: [30, 15],
        });
        L.marker([cLat, cLon], { icon: labelIcon, interactive: false }).addTo(
          blockLabelLayerRef.current
        );
      }

      // modules
      if (showModules) {
        for (let r = 0; r < modulesH; r++) {
          const yCtr =
            ctrLocal[1] - halfH + modW_m / 2 + r * (modW_m + clamp_m);
          const eastTilt = r % 2 === 0;
          const fillColor = eastTilt ? "#bbb" : "#999";
          for (let c = 0; c < modulesW; c++) {
            const xCtr =
              ctrLocal[0] - halfW + modL_m / 2 + c * (modL_m + clamp_m);
            const panelLocal = [
              [xCtr - modL_m / 2, yCtr - modW_m / 2],
              [xCtr + modL_m / 2, yCtr - modW_m / 2],
              [xCtr + modL_m / 2, yCtr + modW_m / 2],
              [xCtr - modL_m / 2, yCtr + modW_m / 2],
            ];
            const panelLatLng = panelLocal.map((pt) => {
              const pENU = rotatePoint(pt, ang);
              const [lat, lon] = enuToLatLon(pENU[0], pENU[1], lat0, lon0);
              return [lat, lon];
            });
            panelLatLng.push(panelLatLng[0]);
            L.polygon(panelLatLng, {
              color: "#666",
              weight: 0.5,
              fillColor,
              fillOpacity: 0.8,
              interactive: false,
              pane: "overlayPane",
            }).addTo(moduleLayerRef.current);
          }
        }
      }
    });

    // Metrics
    const tables = finalPlacements.length;
    const modules = tables * modulesPerTable;
    const kWp = (modules * preset.P_w) / 1000;

    const siteArea_m2 = polygonArea(polyENU);
    const moduleArea_m2 = modules * (modL_m * modW_m);
    const fillRatio = total > 0 ? tables / total : 0;
    const moduleWidth = Math.min(modL_m, modW_m);
    let gcr;
    if (systemType === "peg") {
      gcr = siteArea_m2 > 0 ? moduleArea_m2 / siteArea_m2 : 0;
    } else {
      const baseWidth = systemType === "tracker" ? moduleWidth : tableH_m;
      const baseGcr = rowPitchM > 0 ? baseWidth / rowPitchM : 0;
      gcr = baseGcr * fillRatio;
    }
    const kWpPerHa = siteArea_m2 > 0 ? kWp / (siteArea_m2 / 10000) : 0;

    const annualMWh = (kWp * specificYield) / 1000;

    setMetrics({ tables, modules, kWp, siteArea_m2, gcr, kWpPerHa, annualMWh });
  }

  // ensure map events always use the latest redraw logic
  redrawTablesRef.current = redrawTables;

  const vertexLabelForIndex = (idx) => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const letter = letters[idx % letters.length];
    const suffix = Math.floor(idx / letters.length);
    return suffix === 0 ? letter : `${letter}${suffix + 1}`;
  };

  /* ---------- Draggable handles ---------- */
  function rebuildHandles(latlngs) {
    const map = mapRef.current;
    if (!map) return;

    handleMarkersRef.current.forEach((m) => m.remove());
    handleMarkersRef.current = [];

    if (latlngs.length === 0) return;

    const handleIcon = L.divIcon({ className: "vertex-handle", iconSize: [12, 12] });
    const midpointIcon = L.divIcon({ className: "midpoint-handle", iconSize: [10, 10] });

    latlngs.forEach((latlng, i) => {
      const marker = L.marker(latlng, {
        draggable: true,
        icon: handleIcon,
        zIndexOffset: 1000,
        autoPan: false,
        keyboard: false,
        title: `Vertex ${vertexLabelForIndex(i)}`,
      }).addTo(map);

      marker.on("dragstart", () => map.dragging.disable());
      marker.on("drag", (e) => {
        const live = [...latlngs];
        live[i] = [e.latlng.lat, e.latlng.lng];
        polygonLayerRef.current.setLatLngs(live);
        rebuildLabels(live);
        redrawTablesRef.current(undefined, undefined, live);
      });
      marker.on("dragend", () => {
        map.dragging.enable();
        const updated = polygonLayerRef.current
          .getLatLngs()[0]
          .map((ll) => [round6(ll.lat), round6(ll.lng)]);
        setPolygon(updated);
      });

      handleMarkersRef.current.push(marker);
    });

    if (latlngs.length >= 2) {
      latlngs.forEach((latlng, i) => {
        const next = latlngs[(i + 1) % latlngs.length];
        const midpoint = [(latlng[0] + next[0]) / 2, (latlng[1] + next[1]) / 2];

        const marker = L.marker(midpoint, {
          draggable: true,
          icon: midpointIcon,
          zIndexOffset: 900,
          autoPan: false,
          keyboard: false,
          title: `Insert between ${vertexLabelForIndex(i)} and ${vertexLabelForIndex((i + 1) % latlngs.length)}`,
        }).addTo(map);

        marker.on("dragstart", () => map.dragging.disable());
        marker.on("drag", (e) => {
          const live = e?.latlng || marker.getLatLng();
          if (!live || live.lat == null || live.lng == null) return;

          const inserted = [
            ...latlngs.slice(0, i + 1),
            [live.lat, live.lng],
            ...latlngs.slice(i + 1),
          ];
          polygonLayerRef.current.setLatLngs(inserted);
          rebuildLabels(inserted);
          redrawTablesRef.current(undefined, undefined, inserted);
        });
        const commitInsertedVertex = (latlng) => {
          const candidate = (() => {
            if (!latlng) return marker.getLatLng();
            if (Array.isArray(latlng) && latlng.length >= 2) {
              return { lat: latlng[0], lng: latlng[1] };
            }
            if (latlng.lat != null && latlng.lng != null) return latlng;
            return marker.getLatLng();
          })();

          if (!candidate || candidate.lat == null || candidate.lng == null) return;

          const inserted = [
            ...latlngs.slice(0, i + 1),
            [round6(candidate.lat), round6(candidate.lng)],
            ...latlngs.slice(i + 1),
          ];
          setPolygon(inserted);
        };
        marker.on("dragend", (e) => {
          map.dragging.enable();
          const final = e?.latlng || marker.getLatLng();
          commitInsertedVertex(final);
        });
        marker.on("click", (e) => {
          e.originalEvent?.stopPropagation?.();
          commitInsertedVertex(e?.latlng || marker.getLatLng());
        });

        handleMarkersRef.current.push(marker);
      });
    }
  }

  /* ---------- Vertex labels (A, B, C…) ---------- */
  function rebuildLabels(latlngs) {
    if (!labelLayerRef.current) return;
    labelLayerRef.current.clearLayers();

    latlngs.forEach((ll, i) => {
      const label = vertexLabelForIndex(i);
      const icon = L.divIcon({
        className: "vertex-label",
        html: `<div class="vertex-label-badge">${label}</div>`,
        iconSize: [24, 28],
        iconAnchor: [12, 28],
      });
      L.marker(ll, { icon, interactive: false, zIndexOffset: 500 }).addTo(labelLayerRef.current);
    });
  }

  /* ---------- Coordinate editor actions ---------- */
  const updateVertex = (idx, key, val) => {
    const num = parseFloat(val);
    if (Number.isNaN(num)) return;
    const rounded = round6(num);
    const updated = polygon.map((p, i) =>
      i === idx ? (key === "lat" ? [rounded, p[1]] : [p[0], rounded]) : p
    );
    setPolygon(updated);
  };
  const addVertexAfter = (idx) => {
    const a = polygon[idx];
    const b = polygon[(idx + 1) % polygon.length];
    const mid = [round6((a[0] + b[0]) / 2), round6((a[1] + b[1]) / 2)];
    const updated = [...polygon.slice(0, idx + 1), mid, ...polygon.slice(idx + 1)];
    setPolygon(updated);
  };
  const deleteVertex = (idx) => {
    if (polygon.length === 0) return;
    const updated = polygon.filter((_, i) => i !== idx);
    setPolygon(updated);
  };
  const clearVertices = () => {
    setPolygon([]);
  };
  const toggleAddMode = () => setAddMode((v) => !v);

  /* ---------- UI ---------- */
  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      <style>
        {`
          .leaflet-container { z-index: 0; }
          .vertex-handle {
            background: #ffffff;
            border: 2px solid #ff5d5d;
            border-radius: 50%;
            box-sizing: border-box;
          }
          .midpoint-handle {
            background: #fff8eb;
            border: 2px solid #f59e0b;
            border-radius: 4px;
            box-sizing: border-box;
          }
          .vertex-label-badge {
            color: #fff;
            font-weight: 700;
            font-size: 11px;
            line-height: 18px;
            padding: 0;
            border-radius: 0;
            text-shadow: 0 0 4px rgba(0,0,0,0.55);
            white-space: nowrap;
          }
          .block-label {
            background: rgba(17,24,39,0.8);
            color: #fff;
            font-weight: 600;
            font-size: 12px;
            line-height: 1.2;
            text-align: center;
            padding: 2px 4px;
            border-radius: 4px;
          }
          .dim-label {
            background: #fff;
            border: 1px solid currentColor;
            color: #d7b72aff;
            font-weight: 600;
            font-size: 15px;
            line-height: 16px;
            padding: 0 4px;
            border-radius: 4px;
            white-space: nowrap;
          }
          .dim-label.buffer {
            color: #f97316;
          }
          .panel {
            position: absolute;
            right: 12px;
            z-index: 10000;
            max-height: 80vh;
            overflow: auto;
            background: rgba(255,255,255,0.96);
            box-shadow: 0 6px 18px rgba(0,0,0,0.15);
            border-radius: 12px;
            padding: 12px;
            font: 12px/1.4 system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
            min-width: 280px;
            max-width: 360px;
          }
          .panel h3 { margin: 0 0 8px 0; font-size: 14px; }
          .row { display: grid; grid-template-columns: auto 1fr 1fr auto auto; gap: 6px; align-items: center; margin-bottom: 6px; }
          .vertex-row-badge { min-width: 22px; height: 22px; display:inline-flex; align-items:center; justify-content:center; background:#111827; color:#fff; font-weight:700; font-size:11px; border-radius:6px; }
          .row input, .row select { width: 100%; padding: 4px 6px; border: 1px solid #d0d5dd; border-radius: 8px; min-width: 0; }
          .row button { padding: 4px 8px; border-radius: 8px; border: 1px solid #d0d5dd; background: #f7f7f7; cursor: pointer; }
          .row button:hover { background: #eee; }
          .controls { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .controls > label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; min-width: 0; }
          .controls select { text-overflow: ellipsis; }
          .section { margin-bottom: 12px; }
          .split { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .metrics { position:absolute; left:12px; bottom:12px; z-index:10000; background:rgba(255,255,255,0.94); border-radius:12px; box-shadow:0 6px 18px rgba(0,0,0,0.15); padding:12px; min-width:260px; }
          .metrics h3 { margin:0 0 8px 0; font-size:14px; }
          .metrics .item { display:flex; justify-content:space-between; margin:4px 0; }
          .metrics .item b { margin-left:12px; }
          .btn-primary { background:#0ea5e9; color:white; border:1px solid #0ea5e9; }
          .btn { padding:6px 10px; border-radius:8px; border:1px solid #d0d5dd; cursor:pointer; }
          .btn + .btn { margin-left:6px; }
          .topnav { position:absolute; top:12px; left:50%; transform:translateX(-50%); z-index:20000; background:rgba(255,255,255,0.94); border-radius:8px; padding:4px 8px; display:flex; gap:8px; }
          .topnav button { border:1px solid #d0d5dd; border-radius:6px; padding:4px 8px; background:#fff; cursor:pointer; }
          .topnav button.active { background:#0ea5e9; color:#fff; border-color:#0ea5e9; }
          .financial-page { position:absolute; top:0; left:0; right:0; bottom:0; z-index:15000; padding:60px 20px; overflow:auto; box-sizing:border-box; background:rgba(255,255,255,0.94); }
          .financial-page table { border-collapse:collapse; width:100%; max-width:800px; }
          .financial-page th, .financial-page td { border:1px solid #ccc; padding:4px 8px; text-align:right; }
          .financial-page th:first-child, .financial-page td:first-child { text-align:left; }
          .financial-page input { width:80px; }
          .quote-page { position:absolute; top:0; left:0; right:0; bottom:0; z-index:15000; padding:60px 20px; overflow:auto; box-sizing:border-box; background:rgba(255,255,255,0.94); }
          .quote-page table { border-collapse:collapse; width:100%; max-width:800px; }
          .quote-page th, .quote-page td { border:1px solid #ccc; padding:4px 8px; text-align:right; }
          .quote-page th:first-child, .quote-page td:first-child { text-align:left; }
          .quote-page input { width:80px; }
          .manual-page { position:absolute; top:0; left:0; right:0; bottom:0; z-index:15000; padding:60px 20px; overflow:auto; box-sizing:border-box; background:rgba(255,255,255,0.94); }
          .manual-header { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:12px; margin-bottom:24px; }
          .manual-section { max-width:800px; margin-bottom:24px; }
          .manual-section h2 { margin-bottom:12px; }
          .manual-section p { margin-bottom:8px; line-height:1.5; }
        `}
      </style>
      <div
        ref={mapElRef}
        style={{
          height: "100%",
          width: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
      <div className="topnav">
        <button
          className={page === "design" ? "active" : ""}
          onClick={() => setPage("design")}
        >
          Design
        </button>
        <button
          className={page === "financial" ? "active" : ""}
          onClick={() => setPage("financial")}
        >
          Financial
        </button>
        <button
          className={page === "quote" ? "active" : ""}
          onClick={() => setPage("quote")}
        >
          Quote
        </button>
        <button
          className={page === "manual" ? "active" : ""}
          onClick={() => setPage("manual")}
        >
          Manual
        </button>
      </div>
      {page === "design" ? (
        <>

        {/* Floating: Project */}
        <div className="panel" style={{ top: 12, left: 12, right: "auto" }}>
          <h3>Project</h3>
          <div className="section">
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              Name
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Untitled project"
              />
            </label>
          </div>
          <div className="section">
            <button className="btn btn-primary" onClick={downloadProject}>Download</button>
            <button className="btn" onClick={downloadKmz}>Export KMZ</button>
            <button
              className="btn"
              onClick={downloadPdf}
              disabled={isExportingPdf}
              style={isExportingPdf ? { opacity: 0.6, cursor: "wait" } : undefined}
            >
              {isExportingPdf ? "Building PDF…" : "Export PDF"}
            </button>
            <label className="btn">
              Load
              <input type="file" accept="application/json" onChange={onProjectFile} style={{ display: "none" }} />
            </label>
          </div>
          <div className="section" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className="btn" onClick={() => downloadCad("dxf")}>
              Export DXF
            </button>
            <button className="btn" onClick={() => downloadCad("dwg")}>
              Download DWG bundle
            </button>
          </div>
          {savedAt && (
            <div className="section" style={{ fontSize: 12, color: "#555" }}>
              Saved {new Date(savedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </div>
          )}
        </div>

        {/* Floating: Right-side controls */}
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            zIndex: 10000,
            maxHeight: "calc(100vh - 24px)",
            overflowY: "auto",
          }}
        >
          {/* Floating: Coordinates Editor */}
          <div className="panel" style={{ position: "relative", right: "auto" }}>
            <h3>Polygon vertices</h3>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  id="autofit"
                  checked={autoFit}
                  onChange={(e) => setAutoFit(e.target.checked)}
                />
                <label htmlFor="autofit">Auto-fit</label>
              </span>
              <button className="btn" onClick={clearVertices}>Clear points</button>
              <button
                className="btn btn-primary"
                onClick={toggleAddMode}
                title="Click map to add a point"
                style={{
                  background: addMode ? "#059669" : "#0ea5e9",
                  borderColor: addMode ? "#059669" : "#0ea5e9",
                }}
              >
                {addMode ? "Adding… (click map)" : "Add point"}
              </button>
            </div>

            {polygon.map(([lat, lng], i) => {
              const label = vertexLabelForIndex(i);
              return (
                <div className="row" key={i}>
                  <div className="vertex-row-badge" title={`Vertex ${label}`}>
                    {label}
                  </div>
                  <input
                    type="number"
                    step="0.000001"
                    value={lat.toFixed(6)}
                    onChange={(e) => updateVertex(i, "lat", e.target.value)}
                    title={`Vertex ${label} lat`}
                  />
                  <input
                    type="number"
                    step="0.000001"
                    value={lng.toFixed(6)}
                    onChange={(e) => updateVertex(i, "lng", e.target.value)}
                    title={`Vertex ${label} lng`}
                  />
                  <button title="Add vertex after" onClick={() => addVertexAfter(i)}>+</button>
                  <button
                    title="Delete vertex"
                    onClick={() => deleteVertex(i)}
                    disabled={polygon.length === 0}
                  >
                    –
                  </button>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
              Tip: Use "Clear points" to remove all vertices, pan to a new location, then toggle "Add point" and click the map to start a new shape.
            </div>
          </div>

          {/* Floating: PV Settings (narrowed) */}
          <div className="panel" style={{ position: "relative", right: "auto" }}>
            <h3>PV settings</h3>
            <div className="section controls">
              <label title="System type">
                System
                <select
                  value={systemType}
                  onChange={(e) => handleSystemChange(e.target.value)}
                >
                  <option value="peg">PEG blocks</option>
                  <option value="fixed">Fixed tilt N/S</option>
                  <option value="tracker">Tracker</option>
                </select>
              </label>
              <label title="Module preset">
                Module
                <select value={presetId} onChange={(e) => setPresetId(e.target.value)}>
                  {MOD_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label title="Block orientation">
                Orient
                <select
                  value={landscape ? "landscape" : "portrait"}
                  onChange={(e) => setLandscape(e.target.value === "landscape")}
                >
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </label>
              {systemType === "peg" && (
                <label title="Block size preset">
                  Block size
                  <select
                    value={pegBlockPreset}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPegBlockPreset(val);
                      if (val === "8x12") {
                        setModulesW(8);
                        setModulesH(12);
                      } else if (val === "14x24") {
                        setModulesW(14);
                        setModulesH(24);
                      }
                    }}
                  >
                    <option value="8x12">8 × 12</option>
                    <option value="14x24">14 × 24</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
              )}
              <label title="Modules across (E–W)">
                Mods (W)
                <input
                  type="number"
                  min={1}
                  value={modulesW}
                  disabled={systemType === "peg" && pegBlockPreset !== "custom"}
                  onChange={(e) => setModulesW(Math.max(1, +e.target.value || 1))}
                />
              </label>
              <label title="Modules high (N–S)">
                Mods (H)
                <input
                  type="number"
                  min={1}
                  value={modulesH}
                  disabled={systemType === "peg" && pegBlockPreset !== "custom"}
                  onChange={(e) => setModulesH(Math.max(1, +e.target.value || 1))}
                />
              </label>
              <label title="Clamp gap">
                Clamp (mm)
                <input
                  type="number"
                  min={0}
                  value={clampGapMm}
                  onChange={(e) => setClampGapMm(Math.max(0, +e.target.value || 0))}
                />
              </label>
              <label title={systemType === "peg" ? "Gap N–S" : "Row pitch"}>
                {systemType === "peg" ? "Gap N–S (m)" : "Pitch (m)"}
                <input
                  type="number"
                  step="0.1"
                  min={systemType === "peg" ? 0 : tableH_m}
                  value={gapNS}
                  onChange={(e) =>
                    setGapNS(
                      Math.max(
                        systemType === "peg" ? 0 : tableH_m,
                        +e.target.value || 0
                      )
                    )
                  }
                />
              </label>
              <label title="Gap East–West">
                Gap E–W (m)
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={gapEW}
                  onChange={(e) => setGapEW(Math.max(0, +e.target.value || 0))}
                />
              </label>
              <label title="Inner buffer distance">
                Buffer (m)
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={bufferM}
                  onChange={(e) => setBufferM(Math.max(0, +e.target.value || 0))}
                />
              </label>
              <label title="Layout rotation">
                Rot (deg)
                <input
                  type="number"
                  step="1"
                  value={orientationDeg}
                  onChange={(e) => setOrientationDeg(+e.target.value || 0)}
                />
              </label>
              <label title="Block packing mode">
                Packing
                <select
                  value={alignGrid ? "grid" : "packed"}
                  onChange={(e) => setAlignGrid(e.target.value === "grid")}
                >
                  <option value="grid">Grid</option>
                  <option value="packed">Best fit</option>
                </select>
              </label>
              <label title="Limit filled blocks">
                Fill
                <input
                  type="range"
                  min={0}
                  max={maxFillBlocks}
                  step={1}
                  value={fillBlocks}
                  disabled={fillAll}
                  onChange={(e) => {
                    const val = +e.target.value;
                    setFillBlocks(val);
                    redrawTablesRef.current(val);
                  }}
                />
                <span>
                  {fillBlocks}/{maxFillBlocks}
                </span>
              </label>
              <label title="Fill all blocks">
                <input
                  type="checkbox"
                  checked={fillAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFillAll(checked);
                    if (checked) {
                      const val = maxFillBlocks;
                      setFillBlocks(val);
                      redrawTablesRef.current(val);
                    }
                  }}
                />
                100%
              </label>
              <label title="Fill origin">
                Start
                <select
                  value={fillOrigin}
                  onChange={(e) => {
                    const dir = e.target.value;
                    setFillOrigin(dir);
                    redrawTablesRef.current(fillBlocks, dir);
                  }}
                >
                  <option value="N">N</option>
                  <option value="NE">NE</option>
                  <option value="E">E</option>
                  <option value="SE">SE</option>
                  <option value="S">S</option>
                  <option value="SW">SW</option>
                  <option value="W">W</option>
                  <option value="NW">NW</option>
                </select>
              </label>
              <label title="Specific yield">
                kWh/kWp·yr
                <input
                  type="number"
                  step="10"
                  min={500}
                  value={specificYield}
                  disabled={yieldMode === "auto"}
                  onChange={(e) =>
                    setSpecificYield(Math.max(0, +e.target.value || 0))
                  }
                />
                <select
                  value={yieldMode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setYieldMode(mode);
                    if (mode === "auto") {
                      setSpecificYield(baseYield * YIELD_FACTORS[systemType]);
                    }
                  }}
                >
                  <option value="auto">Auto</option>
                  <option value="manual">Manual</option>
                </select>
              </label>
            </div>

            <div className="section split">
              <div>
                <div style={{ fontSize: 12, color: "#555" }}>Block footprint</div>
                <div>
                  <b>
                    {tableW_m.toFixed(2)} m (W) × {tableH_m.toFixed(2)} m (H)
                  </b>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#555" }}>Pitches (c‑c)</div>
                <div>
                  <b>
                    {columnPitchM.toFixed(2)} m (E–W), {rowPitchM.toFixed(2)} m (N–S)
                  </b>
                </div>
              </div>
            </div>

            <div className="section">
              <button className="btn" onClick={() => setShowOptimizer((s) => !s)}>
                {showOptimizer ? "Hide optimizer" : "Show optimizer"}
              </button>
            </div>
            {showOptimizer && (
              <>
                <div className="section">
                  <label>
                    Optimize for
                    <select
                      value={optMetric}
                      onChange={(e) => setOptMetric(e.target.value)}
                    >
                      <option value="gcr">GCR</option>
                      <option value="annualMWh">Estimated yield</option>
                    </select>
                  </label>
                  <button
                    className="btn btn-primary"
                    onClick={() => optimizeLayout(optMetric)}
                  >
                    Optimize
                  </button>
                </div>
                <div className="section">
                  <div style={{ fontSize: 12, color: "#555" }}>
                    Search space (uncheck to unlock)
                  </div>
                  {boundRow("modulesW", "Modules W")}
                  {boundRow("modulesH", "Modules H")}
                  {systemType === "peg"
                    ? boundRow("gapNS", "Gap N–S (m)", 0.5)
                    : boundRow("gapNS", "Pitch (m)", 0.5)}
                  {boundRow("gapEW", "Gap E-W (m)", 0.5)}
                  {boundRow("orientationDeg", "Rotation (deg)", 5)}
                  <label>
                    <input
                      type="checkbox"
                      checked={optLocks.presetId}
                      onChange={(e) =>
                        setOptLocks((p) => ({ ...p, presetId: e.target.checked }))
                      }
                    />
                    Lock module preset
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={optLocks.landscape}
                      onChange={(e) =>
                        setOptLocks((p) => ({ ...p, landscape: e.target.checked }))
                      }
                    />
                    Lock portrait/landscape
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Floating: Metrics */}
        <div className="metrics">
          {projectName && <div style={{ fontWeight: 700, marginBottom: 4 }}>{projectName}</div>}
          <div style={{ fontSize: 12, color: "#555" }}>{SYSTEM_LABELS[systemType]}</div>
          <h3>Layout metrics</h3>
          <div className="item"><span>Blocks (tables)</span><b>{metrics.tables.toLocaleString()}</b></div>
          <div className="item"><span>Modules</span><b>{metrics.modules.toLocaleString()}</b></div>
          <div className="item">
            <span>DC size</span>
            {(() => {
              const p = formatPower(metrics.kWp);
              return <b>{p.value} {p.unit}</b>;
            })()}
          </div>
        <div className="item"><span>Site area</span><b>{formatNumber(metrics.siteArea_m2, 0)} m²</b></div>
        <div className="item">
          <span>DC density</span>
          {(() => {
            const d = formatPower(metrics.kWpPerHa);
            return <b>{d.value} {d.unit}/ha</b>;
          })()}
        </div>
        <div className="item"><span>GCR</span><b>{formatNumber(metrics.gcr * 100, 1)}%</b></div>
        <div className="item"><span>Est. yield</span><b>{formatNumber(metrics.annualMWh, 1)} MWh/yr</b></div>
        <div className="item"><span>Zoom</span><b>{zoom.toFixed(2)}</b></div>
        <div className="item"><span>Total CAPEX</span><b>${totalCapex.toFixed(0)}</b></div>
        <div className="item"><span>$ / kWp</span><b>${costPerKwp.toFixed(2)}</b></div>
        <div className="item"><span>$ / kWh/yr</span><b>${costPerKwhYear.toFixed(3)}</b></div>
        <div className="item"><span>LCOE</span><b>${lcoe.toFixed(3)}</b></div>
      </div>
      </>
      ) : page === "financial" ? (
        <FinancialPage
          systemType={systemType}
          setSystemType={handleSystemChange}
          costs={costs}
          setCosts={setCosts}
          kWp={metrics.kWp}
          annualMWh={metrics.annualMWh}
          siteAreaHa={metrics.siteArea_m2 / 10000}
          currency={currency}
          setCurrency={setCurrency}
          financials={financials}
          setFinancials={setFinancials}
        />
      ) : page === "quote" ? (
        <QuotePage
          systemType={systemType}
          costs={costs}
          kWp={metrics.kWp}
          currency={currency}
        />
      ) : (
        <UserManual />
      )}
    </div>
  );
}
